/**
 * Solo-Simulator — 1:1 System-Originale (Kurzauswertung + Kalkulator).
 * Schreibt ausschließlich in useSoloSimulationStore (+ lokaler Kalk-State).
 * KEINE Writes auf clientData / appStore / Firestore.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useAppStore from '../store/appStore';
import useAdminStore from '../store/adminStore';
import { useCalculationsSelector } from '../store/selectors/calculations';
import { auth } from '../config/firebase';
import UI from '../components/UI';
import { evaluateRentabilitaet } from '../store/utils';
import {
  computeSoloDerived,
  useSoloSimulationStore,
  type SoloSimulationDerived,
  type SoloSimulationKnobs,
  type SoloSimulationSnapshot,
} from '../store/soloSimulationStore';
import { buildSoloSnapshotFromLive } from '../utils/soloSimulationSnapshot';
import { currencyLabel, formatMoney } from '../utils/currencyFormatter';

/* ─── Helpers (1:1 aus KalkulatorPage) ─── */
const parseNumber = (text: any): number => {
  if (typeof text === 'number') return isNaN(text) ? 0 : text;
  if (!text) return 0;
  let s = String(text).replace(/<[^>]*>/g, ' ').replace(/\u00a0/g, ' ').trim();
  const hasComma = s.includes(',');
  const hasDot = s.includes('.');
  if (hasDot && hasComma && s.lastIndexOf('.') < s.lastIndexOf(',')) {
    s = s.replace(/\./g, '').replace(',', '.');
  } else if (hasComma) {
    s = s.replace(',', '.');
  }
  const cleaned = s.replace(/[^0-9.-]/g, '');
  const number = parseFloat(cleaned);
  return isNaN(number) ? 0 : number;
};

const formatMinutes = (num: number): string =>
  `${isNaN(num) ? 0 : num.toLocaleString('de-DE')} min`;
const formatGramm = (num: number): string =>
  `${isNaN(num) ? 0 : num.toLocaleString('de-DE')} g`;
const formatPercent = (num: number, digits: number = 1): string =>
  isNaN(num)
    ? '0,0%'
    : num.toLocaleString('de-DE', {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
      }) + '%';

const symbolToText = (formattedValue: string): string =>
  formattedValue.replace('€', 'EUR').replace('CHF', 'CHF');

/** System-Währung: immer „15,24 EUR“ / „15,24 CHF“ — nie €-Zeichen */
const makeFormatCurrency =
  (currencyCode: string) =>
  (value: number, decimals = 2): string =>
    formatMoney(Number.isFinite(value) ? value : 0, currencyCode, decimals);

/* ─── Styles (Carbon + Gauge + Anpassung — aus KalkulatorPage) ─── */
const SoloSystemStyles = () => (
  <style>{`
    :root {
      --custom-beige: #E6C299;
    }
    .text-customBeige { color: var(--custom-beige); text-shadow: 0 0 2px rgba(0,0,0,0.5); }
    .font-numeric {
      font-family: 'JetBrains Mono', monospace;
      font-variant-numeric: tabular-nums;
    }
    .editable-field {
      background: linear-gradient(145deg, #000000, #0a0a0a);
      color: white;
      border: 1px solid #222;
      border-radius: 0.5rem;
      padding: 0.5rem;
      text-align: center;
      cursor: text;
      min-width: 60px;
      width: 100%;
      box-sizing: border-box;
      font-family: 'JetBrains Mono', monospace;
      transition: all 0.2s;
      box-shadow: inset 2px 2px 5px rgba(0,0,0,0.8), inset -1px -1px 2px rgba(255,255,255,0.05);
      -webkit-appearance: none;
      appearance: none;
    }
    .editable-field:focus {
      outline: none;
      border-color: var(--custom-beige);
      box-shadow: 0 0 0 1px var(--custom-beige), inset 2px 2px 5px rgba(0,0,0,0.8);
    }
    .step-field-wrap {
      display: flex;
      align-items: center;
      gap: 0.25rem;
      width: 100%;
      min-width: 0;
      min-height: 2.25rem;
    }
    .step-field-wrap .editable-field {
      flex: 1 1 auto;
      min-width: 0;
      align-self: center;
    }
    .step-btn {
      flex: 0 0 auto;
      width: 2rem;
      min-width: 2rem;
      height: 2.25rem;
      min-height: 2.25rem;
      padding: 0;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      background: linear-gradient(145deg, #111, #000);
      color: var(--custom-beige);
      border: 1px solid #222;
      border-radius: 0.5rem;
      font-size: 1.05rem;
      font-weight: 800;
      line-height: 1;
      cursor: pointer;
      user-select: none;
      -webkit-tap-highlight-color: transparent;
      touch-action: manipulation;
      box-shadow: 2px 2px 5px rgba(0,0,0,0.5), -1px -1px 2px rgba(255,255,255,0.05);
      transition: all 0.15s;
    }
    .step-btn:hover {
      border-color: var(--custom-beige);
      color: #000;
      background: var(--custom-beige);
    }
    .step-btn:active {
      transform: scale(0.94);
      box-shadow: inset 2px 2px 5px rgba(0,0,0,0.5);
    }
    .step-field-compact .step-btn {
      width: 1.65rem;
      min-width: 1.65rem;
      height: 2rem;
      min-height: 2rem;
      font-size: 0.95rem;
    }
    .step-field-compact.step-field-wrap {
      min-height: 2rem;
    }
    @media (min-width: 640px) {
      .step-btn {
        width: 2.25rem;
        min-width: 2.25rem;
        height: 2.5rem;
        min-height: 2.5rem;
        font-size: 1.15rem;
      }
      .step-field-wrap {
        gap: 0.35rem;
        min-height: 2.5rem;
      }
      .step-field-compact .step-btn {
        width: 2rem;
        min-width: 2rem;
        height: 2.35rem;
        min-height: 2.35rem;
      }
      .step-field-compact.step-field-wrap {
        min-height: 2.35rem;
      }
    }
    .calculated-field {
      background: linear-gradient(145deg, #0a0a0a, #111);
      color: var(--custom-beige);
      border: 1px solid #222;
      border-radius: 0.5rem;
      padding: 0.5rem;
      text-align: center;
      font-family: 'JetBrains Mono', monospace;
      box-shadow: inset 2px 2px 5px rgba(0,0,0,0.8);
    }
    .carbon-weave-bg {
      background-color: #0a0a0a;
      background-image:
        linear-gradient(45deg, #111 25%, transparent 25%, transparent 75%, #111 75%, #111),
        linear-gradient(45deg, #111 25%, transparent 25%, transparent 75%, #111 75%, #111),
        radial-gradient(circle at 50% 0, #1a1a1a 5%, #111 5%, #111 15%, #1a1a1a 15%, #1a1a1a 20%, #111 20%, #111 29%, #1a1a1a 29%, #1a1a1a 34%, #111 34%, #111 44%, #1a1a1a 44%, #1a1a1a 49%, transparent 49%, transparent),
        radial-gradient(circle at 0 50%, #1a1a1a 5%, #111 5%, #111 15%, #1a1a1a 15%, #1a1a1a 20%, #111 20%, #111 29%, #1a1a1a 29%, #1a1a1a 34%, #111 34%, #111 44%, #1a1a1a 44%, #1a1a1a 49%, transparent 49%, transparent);
      background-size: 20px 20px;
    }
    .carbon-panel {
      position: relative;
      border-radius: 1.5rem;
      border: 1px solid #1a1a1a;
      box-shadow: 0 30px 60px -15px rgba(0,0,0,1), inset 0 2px 5px rgba(255,255,255,0.02);
      overflow: hidden;
      z-index: 1;
    }
    .carbon-panel::after {
      content: '';
      position: absolute;
      inset: 0;
      background: linear-gradient(to bottom, transparent, rgba(0,0,0,0.55));
      pointer-events: none;
      z-index: 1;
    }
    .carbon-content { position: relative; z-index: 10; }
    .gauge-container-3d {
      background: radial-gradient(circle at center, #111 0%, #030303 100%);
      box-shadow: inset 0 0 40px rgba(0,0,0,0.95), 0 10px 25px rgba(0,0,0,0.6);
      border: 1px solid #1a1a1a;
      border-radius: 1.5rem;
      padding-top: 0.5rem;
      padding-bottom: 0.5rem;
    }
    .text-verlust { color: #ef4444 !important; text-shadow: 0 0 15px rgba(239, 68, 68, 0.6) !important; }
    .text-kostendeckend { color: #facc15; text-shadow: 0 0 15px rgba(250, 204, 21, 0.6) !important; }
    .text-gewinn { color: #22c55e; text-shadow: 0 0 15px rgba(34, 197, 94, 0.6) !important; }
    .text-gut { color: #3b82f6; text-shadow: 0 0 15px rgba(59, 130, 246, 0.6) !important; }
    .text-exzellent { color: #8b5cf6; text-shadow: 0 0 15px rgba(139, 92, 246, 0.6) !important; }
    .anpassung-ok { color: #60a5fa; }
    .anpassung-moderate { color: #fbbf24; }
    .anpassung-critical { color: #f87171; }
    circle#solo-gewinn-gauge-progress {
      transition: stroke-dashoffset 0.8s cubic-bezier(0.4, 0, 0.2, 1), stroke 0.5s ease-in-out;
    }
    html:not(.dark) .text-customBeige { color: var(--pe-accent-text); text-shadow: none; }
    html:not(.dark) .editable-field {
      background: var(--pe-bg-input);
      color: var(--pe-text-primary);
      border-color: var(--pe-border-strong);
      box-shadow: inset 0 1px 3px rgba(28,25,23,0.1);
    }
    html:not(.dark) .step-btn {
      background: var(--pe-bg-card);
      color: var(--pe-accent-text);
      border-color: var(--pe-border-strong);
      box-shadow: 0 1px 2px rgba(28,25,23,0.08);
    }
    html:not(.dark) .step-btn:hover {
      background: var(--pe-accent-text);
      color: #fff;
      border-color: var(--pe-accent-text);
    }
    html:not(.dark) .calculated-field {
      background: var(--pe-bg-tile-header);
      color: var(--pe-accent-text);
      border-color: var(--pe-border-subtle);
      box-shadow: inset 0 1px 3px rgba(28,25,23,0.08);
    }
    html:not(.dark) .carbon-weave-bg {
      background-color: var(--pe-bg-elevated);
      background-image: none;
    }
    html:not(.dark) .carbon-panel {
      border-color: var(--pe-border-subtle);
      box-shadow: 0 20px 45px -20px rgba(28, 25, 23, 0.18), 0 2px 8px rgba(28, 25, 23, 0.06);
    }
    html:not(.dark) .carbon-panel::after { background: none; }
    html:not(.dark) .gauge-container-3d {
      background: var(--pe-bg-elevated);
      box-shadow: inset 0 1px 8px rgba(28,25,23,0.08);
      border-color: var(--pe-border-subtle);
    }
    html:not(.dark) .anpassung-ok { color: #1E3A8A; }
    html:not(.dark) .text-gewinn { color: #15803D; text-shadow: none !important; }
    html:not(.dark) .text-kostendeckend { color: #A16207; text-shadow: none !important; }
    html:not(.dark) .text-verlust { color: #B91C1C !important; text-shadow: none !important; }
  `}</style>
);

/* ─── Inline EditableField (1:1 Kalkulator) ─── */
const EditableField = ({
  value,
  onSave,
  format,
  className,
  placeholder,
  type = 'number',
  readOnly = false,
  formatCurrency,
}: any) => {
  const [draft, setDraft] = useState('');
  const [focused, setFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const formatValue = useCallback(
    (val: any, fmt: string) => {
      const numVal = parseNumber(val);
      switch (fmt) {
        case 'currency': {
          const formatted = formatCurrency
            ? formatCurrency(numVal)
            : formatMoney(numVal, 'EUR', 2);
          return symbolToText(formatted);
        }
        case 'minutes':
          return formatMinutes(numVal);
        case 'gramm':
          return formatGramm(numVal);
        case 'percent':
          return formatPercent(numVal, 1);
        default:
          return String(val ?? '');
      }
    },
    [formatCurrency]
  );

  const displayValue = type === 'text' ? String(value ?? '') : formatValue(value, format);

  useEffect(() => {
    if (!focused) setDraft(displayValue);
  }, [displayValue, focused]);

  if (readOnly) {
    return <div className={`calculated-field ${className || ''}`}>{displayValue}</div>;
  }

  const commit = (raw: string) => {
    if (type === 'text') {
      onSave(raw);
      setDraft(raw);
      return;
    }
    const numValue = parseNumber(raw);
    onSave(numValue);
    setDraft(formatValue(numValue, format));
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setFocused(true);
    const editVal =
      type === 'text' ? String(value ?? '') : String(parseNumber(value)).replace('.', ',');
    setDraft(editVal);
    requestAnimationFrame(() => {
      try {
        e.target.select();
      } catch {
        /* ignore */
      }
    });
  };

  const mobileInputMode =
    type === 'text' ? 'text' : format === 'minutes' ? 'numeric' : 'decimal';

  return (
    <input
      ref={inputRef}
      type="text"
      inputMode={mobileInputMode}
      enterKeyHint="done"
      autoComplete="off"
      autoCorrect="off"
      autoCapitalize="off"
      spellCheck={false}
      lang="de-DE"
      value={focused ? draft : displayValue}
      placeholder={placeholder || ''}
      className={`editable-field ${className || ''}`}
      onFocus={handleFocus}
      onBlur={() => {
        setFocused(false);
        commit(draft);
      }}
      onChange={(e) => setDraft(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          e.currentTarget.blur();
        }
      }}
    />
  );
};

const SteppedEditableField = ({
  value,
  onSave,
  step = 5,
  min = 0,
  format,
  className,
  formatCurrency,
  ariaLabel,
  compact = false,
}: {
  value: number;
  onSave: (v: number) => void;
  step?: number;
  min?: number;
  format?: string;
  className?: string;
  formatCurrency?: (v: number, d?: number) => string;
  ariaLabel?: string;
  compact?: boolean;
}) => {
  const nudge = (dir: -1 | 1) => {
    const cur = Number(value);
    const base = Number.isFinite(cur) ? cur : 0;
    let next: number;
    if (step >= 1) {
      const rem = ((base % step) + step) % step;
      const isAligned = rem < 0.01 || step - rem < 0.01;
      if (!isAligned) {
        next = dir > 0 ? Math.ceil(base / step) * step : Math.floor(base / step) * step;
      } else {
        next = Math.round((base + dir * step) / step) * step;
      }
    } else {
      next = Math.round((base + dir * step) * 100) / 100;
    }
    if (next < min) next = min;
    onSave(next);
  };

  return (
    <div className={`step-field-wrap${compact ? ' step-field-compact' : ''}`}>
      <button
        type="button"
        className="step-btn"
        aria-label={`${ariaLabel || 'Wert'} verringern`}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          nudge(-1);
        }}
      >
        −
      </button>
      <EditableField
        value={value}
        onSave={onSave}
        format={format}
        className={className}
        formatCurrency={formatCurrency}
      />
      <button
        type="button"
        className="step-btn"
        aria-label={`${ariaLabel || 'Wert'} erhöhen`}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          nudge(1);
        }}
      >
        +
      </button>
    </div>
  );
};

/* ============================================================================
 * ZONE 2 — Kurzauswertung 1:1 aus KostenstrukturPage
 * ========================================================================== */
type SoloKurzCalc = {
  gemeinkostenfaktor: number;
  materialkostenfaktor: number;
  geraetekostenfaktor: number;
  prodLohnkostenfaktor: number;
  /** Live-Akte „vorher“ (Faktoren) */
  prevGemeinkostenfaktor: number;
  prevMaterialkostenfaktor: number;
  prevGeraetekostenfaktor: number;
  prevProdLohnkostenfaktor: number;
  monatsumsatzNetto: number;
  monatsumsatzBrutto: number;
  sollumsatzNetto: number;
  sollumsatzBrutto: number;
  /** Realistischer Ø-Monatsbedarf bei nur 10 produktiven Monaten (×1,2) */
  realistischNetto: number;
  realistischBrutto: number;
  ueberschuss: number;
  ueberschussProzent: number;
  gesamtlohn: number;
  rentabilitaetSoll: number;
  inhaberErtragSoll: number;
  currencyCode: string;
};

const SoloKurzauswertung = ({ calc }: { calc: SoloKurzCalc }) => {
  const formatValue = (val: number, decimals: number = 0) =>
    new Intl.NumberFormat('de-DE', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    }).format(val || 0);

  const cur = calc.currencyCode || 'EUR';

  const Card = ({
    label,
    value,
    prevValue,
    icon,
    color,
    isPercentage = false,
    decimalPlaces = 2,
  }: any) => {
    const delta =
      isPercentage && Number.isFinite(prevValue) && Number.isFinite(value)
        ? value - prevValue
        : null;
    return (
      <div
        className={`bg-pe-card dark:bg-[#15171e] p-4 rounded-xl shadow-lg flex items-center border-l-4 ${color} border-y border-r border-pe-border dark:border-slate-800 text-left`}
      >
        <div className="mr-3 text-pe-muted dark:text-slate-400">{icon}</div>
        <div className="flex-1 text-left min-w-0">
          <div className="text-slate-500 uppercase font-bold text-[10px] tracking-wider">{label}</div>
          <div className="font-bold text-pe-text dark:text-white text-lg flex items-baseline gap-1 font-mono-numbers">
            {isPercentage ? (
              `${formatValue(value, 1)}%`
            ) : (
              <>
                {formatValue(value, decimalPlaces)}{' '}
                <span className="text-sm text-stone-500 dark:text-slate-500 font-sans ml-1">
                  {cur}
                </span>
              </>
            )}
          </div>
          {isPercentage && prevValue !== undefined && prevValue !== null ? (
            <div className="mt-1 flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-[10px] font-bold uppercase tracking-wider">
              <span className="text-stone-500 dark:text-slate-500">
                Vorher {formatValue(prevValue, 1)}%
              </span>
              {delta !== null ? (
                <span
                  className={
                    delta < -0.05
                      ? 'text-emerald-600 dark:text-emerald-400'
                      : delta > 0.05
                        ? 'text-amber-600 dark:text-amber-400'
                        : 'text-stone-500 dark:text-slate-500'
                  }
                >
                  {delta > 0 ? '+' : ''}
                  {formatValue(delta, 1)} Pp
                </span>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    );
  };

  /** Umsatz / Kosten: Netto + Brutto nebeneinander (Steuerung der Stellschrauben) */
  const DualMoneyCard = ({
    label,
    netto,
    brutto,
    icon,
    color,
    highlight,
  }: {
    label: string;
    netto: number;
    brutto: number;
    icon: React.ReactNode;
    color: string;
    /** Optional: dritter, stärker konturierter Wert (z. B. Ø 10 Mon.) */
    highlight?: { label: string; hint?: string; netto: number; brutto: number };
  }) => (
    <div
      className={`bg-pe-card dark:bg-[#15171e] p-4 rounded-xl shadow-lg flex items-start border-l-4 ${color} border-y border-r border-pe-border dark:border-slate-800 text-left`}
    >
      <div className="mr-3 text-pe-muted dark:text-slate-400 mt-1">{icon}</div>
      <div className="flex-1 text-left min-w-0">
        <div className="text-slate-500 uppercase font-bold text-[10px] tracking-wider mb-2">
          {label}
        </div>
        <div className={`grid gap-3 ${highlight ? 'grid-cols-1 sm:grid-cols-2' : 'grid-cols-1'}`}>
          {/* Linke Box: Netto + Brutto (System-Rahmen) */}
          <div className="rounded-lg border border-pe-border dark:border-slate-700 bg-pe-elevated/60 dark:bg-[#0f1015]/80 px-3 py-2.5 h-full flex flex-col justify-center">
            {highlight ? (
              <div className="text-[10px] font-bold uppercase tracking-widest text-stone-500 dark:text-slate-500 mb-2 leading-tight">
                Ist-Umsatz
              </div>
            ) : null}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="text-[10px] font-bold uppercase tracking-widest text-stone-500 dark:text-slate-500 mb-0.5">
                  Netto
                </div>
                <div className="font-bold text-pe-text dark:text-white text-lg font-mono-numbers flex items-baseline gap-1 whitespace-nowrap">
                  {formatValue(netto, 0)}
                  <span className="text-sm text-stone-500 dark:text-slate-500 font-sans">{cur}</span>
                </div>
              </div>
              <div>
                <div className="text-[10px] font-bold uppercase tracking-widest text-stone-500 dark:text-slate-500 mb-0.5">
                  Brutto
                </div>
                <div className="font-bold text-pe-text dark:text-white text-lg font-mono-numbers flex items-baseline gap-1 whitespace-nowrap">
                  {formatValue(brutto, 0)}
                  <span className="text-sm text-stone-500 dark:text-slate-500 font-sans">{cur}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Rechte Box: gleiche Struktur, gold konturiert */}
          {highlight ? (
            <div className="rounded-lg border-2 border-pe-accent-text/60 dark:border-[#E6C299]/55 bg-pe-accent-text/10 dark:bg-[#E6C299]/10 px-3 py-2.5 h-full flex flex-col justify-center">
              <div className="text-[10px] font-black uppercase tracking-widest text-pe-accent-text dark:text-[#E6C299] mb-2 leading-tight">
                {highlight.label}
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-widest text-pe-accent-text/80 dark:text-[#E6C299]/80 mb-0.5">
                    Netto
                  </div>
                  <div className="font-bold text-pe-accent-text dark:text-[#E6C299] text-lg font-mono-numbers flex items-baseline gap-1 whitespace-nowrap">
                    {formatValue(highlight.netto, 0)}
                    <span className="text-sm font-sans opacity-80">{cur}</span>
                  </div>
                </div>
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-widest text-pe-accent-text/80 dark:text-[#E6C299]/80 mb-0.5">
                    Brutto
                  </div>
                  <div className="font-bold text-pe-accent-text dark:text-[#E6C299] text-lg font-mono-numbers flex items-baseline gap-1 whitespace-nowrap">
                    {formatValue(highlight.brutto, 0)}
                    <span className="text-sm font-sans opacity-80">{cur}</span>
                  </div>
                </div>
              </div>
              {highlight.hint ? (
                <div className="text-[10px] text-pe-muted dark:text-slate-400 mt-2 leading-snug">
                  {highlight.hint}
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );

  const rentabilityStatus = useMemo(() => {
    const gehalt = calc.gesamtlohn || 0;
    const ueberschussProzent = calc.ueberschussProzent || 0;
    const evaluation = evaluateRentabilitaet(gehalt, ueberschussProzent);
    const base = { label: evaluation.status.toUpperCase(), message: evaluation.message };

    if (evaluation.status === 'Kritisch')
      return {
        ...base,
        color: 'text-red-700 dark:text-red-400',
        bgColor: 'bg-red-100/80 dark:bg-red-950/20',
        borderColor: 'border-red-300 dark:border-red-900/50',
      };
    if (evaluation.status === 'Grenzwertig')
      return {
        ...base,
        color: 'text-amber-700 dark:text-amber-400',
        bgColor: 'bg-amber-100/80 dark:bg-amber-950/20',
        borderColor: 'border-amber-300 dark:border-amber-900/50',
      };
    if (evaluation.status === 'Solide')
      return {
        ...base,
        color: 'text-lime-700 dark:text-lime-400',
        bgColor: 'bg-lime-100/80 dark:bg-lime-950/20',
        borderColor: 'border-lime-300 dark:border-lime-900/50',
      };
    if (evaluation.status === 'Gut')
      return {
        ...base,
        color: 'text-emerald-700 dark:text-emerald-400',
        bgColor: 'bg-emerald-100/80 dark:bg-emerald-950/20',
        borderColor: 'border-emerald-300 dark:border-emerald-900/50',
      };

    return {
      ...base,
      color: 'text-purple-700 dark:text-purple-400',
      bgColor: 'bg-purple-100/80 dark:bg-purple-950/20',
      borderColor: 'border-purple-300 dark:border-purple-900/50',
    };
  }, [calc.gesamtlohn, calc.ueberschussProzent]);

  const status = useMemo(() => {
    const ueberschussProzent = calc.ueberschussProzent || 0;
    const target = 10;
    if (ueberschussProzent >= target)
      return {
        text: 'Ziel erreicht',
        color: 'text-emerald-700 dark:text-emerald-400',
        icon: (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
          </svg>
        ),
        bgColor: 'bg-emerald-100/80 dark:bg-emerald-950/20',
        borderColor: 'border-emerald-300 dark:border-emerald-900/50',
      };
    if (ueberschussProzent > 0)
      return {
        text: 'Kostendeckend',
        color: 'text-amber-700 dark:text-amber-400',
        icon: (
          <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path d="M8 12h.01M12 12h.01M16 12h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        ),
        bgColor: 'bg-amber-100/80 dark:bg-amber-950/20',
        borderColor: 'border-amber-300 dark:border-amber-900/50',
      };
    return {
      text: 'Verlust',
      color: 'text-red-700 dark:text-red-400',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path d="M13 17h8m0 0V9m0 8l-8-8-4-4-6-6" />
        </svg>
      ),
      bgColor: 'bg-red-100/80 dark:bg-red-950/20',
      borderColor: 'border-red-300 dark:border-red-900/50',
    };
  }, [calc.ueberschussProzent]);

  return (
    <UI.Section className="flex flex-col gap-6 text-left">
      <UI.SectionTitle>Kurzauswertung</UI.SectionTitle>

      <p className="text-sm text-pe-muted dark:text-slate-400 font-medium leading-relaxed text-left -mt-2">
        Hierbei handelt es sich um den kalkulatorischen Startpunkt und somit um eine Prognose zu
        Jahresbeginn. Wie sich das Jahr tatsächlich entwickelt und hoffentlich verbessert, lässt
        sich auf den Leistungsseiten ersehen.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-left">
        <Card
          label="Gemeinkosten"
          value={(calc.gemeinkostenfaktor || 0) * 100}
          prevValue={(calc.prevGemeinkostenfaktor || 0) * 100}
          isPercentage={true}
          color="border-cyan-500"
          icon={
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
          }
        />
        <Card
          label="Material"
          value={(calc.materialkostenfaktor || 0) * 100}
          prevValue={(calc.prevMaterialkostenfaktor || 0) * 100}
          isPercentage={true}
          color="border-teal-500"
          icon={
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
            </svg>
          }
        />
        <Card
          label="Geräte"
          value={(calc.geraetekostenfaktor || 0) * 100}
          prevValue={(calc.prevGeraetekostenfaktor || 0) * 100}
          isPercentage={true}
          color="border-slate-500"
          icon={
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
          }
        />
        <Card
          label="Lohn (prod.)"
          value={(calc.prodLohnkostenfaktor || 0) * 100}
          prevValue={(calc.prevProdLohnkostenfaktor || 0) * 100}
          isPercentage={true}
          color="border-indigo-500"
          icon={
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
          }
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-left">
        <DualMoneyCard
          label="Gesamtumsatz"
          netto={calc.monatsumsatzNetto}
          brutto={calc.monatsumsatzBrutto}
          color="border-emerald-500"
          highlight={{
            label: 'Ø realistisch (10 Mon.)',
            hint: 'Jahreskosten ÷ 10 produktive Monate',
            netto: calc.realistischNetto,
            brutto: calc.realistischBrutto,
          }}
          icon={
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          }
        />
        <DualMoneyCard
          label="Gesamtkosten (Sollumsatz)"
          netto={calc.sollumsatzNetto}
          brutto={calc.sollumsatzBrutto}
          color="border-red-500"
          icon={
            <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
          }
        />
      </div>

      <div className="space-y-4 mt-2 text-left">
        <div
          className={`rounded-xl p-5 shadow-lg flex items-center border ${status.borderColor} ${status.bgColor} w-full text-left`}
        >
          <div className={`flex-shrink-0 mr-6 ${status.color} scale-125`}>{status.icon}</div>
          <div className="flex-1 text-left">
            <div className="text-xs text-pe-muted dark:text-slate-400 uppercase font-bold tracking-widest text-left mb-1">
              kalk. Überschuss / Verlust
            </div>
            <div className="flex justify-start items-baseline space-x-3 text-left">
              <span className={`text-4xl font-black font-mono-numbers ${status.color}`}>
                {formatValue(calc.ueberschuss, 0)}
              </span>
              <span className="text-xl text-stone-500 dark:text-slate-500 font-bold uppercase">
                {calc.currencyCode || 'EUR'}
              </span>
              <span className={`text-xl font-black font-mono-numbers ${status.color} opacity-80 ml-2`}>
                ({formatValue(calc.ueberschussProzent || 0, 1)}%)
              </span>
            </div>
          </div>
          <div className="text-right">
            <div className="text-[10px] text-stone-500 dark:text-slate-500 uppercase font-bold tracking-widest">
              Ziel: mind. 10%
            </div>
            <div className={`font-black tracking-widest uppercase text-xl mt-1 ${status.color}`}>
              {status.text}
            </div>
          </div>
        </div>

        <div
          className={`rounded-xl p-5 shadow-lg flex flex-col border ${rentabilityStatus.borderColor} ${rentabilityStatus.bgColor} w-full transition-all duration-300 text-left`}
        >
          <div className="flex items-center w-full">
            <div className={`flex-shrink-0 mr-6 ${rentabilityStatus.color} scale-110`}>
              <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
              </svg>
            </div>
            <div className="flex-1 text-left">
              <div className="text-[10px] text-pe-muted dark:text-slate-400 uppercase font-bold tracking-widest text-left mb-1">
                Kalkulatorische Rentabilität
              </div>
              <div className="flex justify-start items-baseline space-x-2 text-left">
                <span className={`text-3xl font-black font-mono-numbers ${rentabilityStatus.color}`}>
                  {formatValue(calc.rentabilitaetSoll, 1)}%
                </span>
              </div>
            </div>
            <div className="text-right">
              <div className="text-[10px] text-stone-500 dark:text-slate-500 uppercase font-bold tracking-widest">
                Status
              </div>
              <div className={`font-black text-sm uppercase tracking-widest mt-1 ${rentabilityStatus.color}`}>
                {rentabilityStatus.label}
              </div>
            </div>
          </div>
          <div
            className={`mt-4 pt-3 border-t ${rentabilityStatus.borderColor} text-xs font-medium leading-relaxed ${rentabilityStatus.color} opacity-90`}
          >
            {rentabilityStatus.message}
          </div>
        </div>

        <div className="bg-pe-elevated dark:bg-[#0f1015] border border-pe-border dark:border-slate-800 rounded-xl p-5 text-left">
          <div className="flex items-start gap-4">
            <div className="text-blue-700 dark:text-blue-400 mt-1 shrink-0">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="16" x2="12" y2="12"></line>
                <line x1="12" y1="8" x2="12.01" y2="8"></line>
              </svg>
            </div>
            <div className="text-xs text-pe-muted dark:text-slate-400 leading-relaxed font-medium text-left">
              Kalkulatorischer Inhaber-Ertrag von{' '}
              <span className="text-pe-accent-text dark:text-[#E6C299] font-bold">
                {formatValue(calc.inhaberErtragSoll, 0)} {calc.currencyCode || 'EUR'} pro Monat
              </span>{' '}
              (vor Steuern). Der Wert beinhaltet Deinen Überschuss, Entnahmen und kalkulierte
              Lohnsteuer.{' '}
              <span className="block mt-1 text-blue-700 dark:text-blue-400 font-bold">
                Bespreche die finale Steuerlast bitte mit Deinem Steuerberater.
              </span>
            </div>
          </div>
        </div>
      </div>
    </UI.Section>
  );
};

/* ============================================================================
 * ZONE 3 — Kalkulator 1:1 aus KalkulatorPage (Solo-Datenquelle)
 * ========================================================================== */
type MatItem = { id: string; name: string; gebinde: number; ekPreis: number };
type DevItem = { id: string; name: string; kostenProzent: number };

/** Brutto-Preis, bei dem Solo-Gewinn ≈ 0 (Kostendeckung). */
function calcSoloBreakEvenBrutto(opts: {
  bedienzeit: number;
  materialPreis: number;
  materialGramm: number;
  activeMaterialIDs: Set<string>;
  materialData: MatItem[];
  activeDeviceIDs: Set<string>;
  geraeteListe: DevItem[];
  mwstSatz: number;
  gemeinkostenfaktor: number;
  verlustPercent: number;
  matFaktor: number;
  prodStundenlohnChef: number;
  lohnfortzahlungPercent: number;
}): number {
  const mwst = (opts.mwstSatz || 0) / 100;
  const gk = opts.gemeinkostenfaktor || 0;
  const vl = (opts.verlustPercent || 0) / 100;
  const ul = 0; // Solo: unpr. Unternehmerlohn = 0

  let mSum = 0;
  let mCnt = 0;
  opts.activeMaterialIDs.forEach((id) => {
    const m = opts.materialData.find((x) => x.id === id);
    if (m && m.gebinde > 0) {
      mSum += (m.ekPreis / m.gebinde) * 100;
      mCnt++;
    }
  });
  const avg100 = mCnt > 0 ? mSum / mCnt : 0;
  const matCost =
    ((avg100 / 100) * (opts.materialGramm || 0) + (opts.materialPreis || 0)) *
    (opts.matFaktor > 0 ? opts.matFaktor : 1.3);

  let dPerc = 0;
  opts.activeDeviceIDs.forEach((id) => {
    const d = opts.geraeteListe.find((x) => x.id === id);
    if (d) dPerc += d.kostenProzent;
  });

  const factor = 1 - dPerc / 100 - gk - ul - vl;
  if (!(factor > 0.001)) return 0;

  const bedienzeit = opts.bedienzeit > 0 ? opts.bedienzeit : 60;
  const lohnBasis = Number(((opts.prodStundenlohnChef || 0) * (bedienzeit / 60)).toFixed(2));
  const urlaubVal = Number((lohnBasis * ((opts.lohnfortzahlungPercent || 0) / 100)).toFixed(2));
  const totalLohn = Number((lohnBasis + urlaubVal).toFixed(2));

  const nettoNeeded = (totalLohn + matCost) / factor;
  if (!(nettoNeeded > 0) || !Number.isFinite(nettoNeeded)) return 0;
  return Number((nettoNeeded * (1 + mwst)).toFixed(2));
}

const SoloKalkulatorInputs = ({
  inputs,
  setInputs,
  formatCurrency,
  onManualNeuerPreis,
  onChangeBedienzeit,
}: {
  inputs: any;
  setInputs: React.Dispatch<React.SetStateAction<any>>;
  formatCurrency: (v: number, d?: number) => string;
  /** Wenn der User den neuen Preis selbst ändert — Auto-Break-even stoppen */
  onManualNeuerPreis?: () => void;
  onChangeBedienzeit?: (nextMins: number) => void;
}) => {
  const { bisherigerPreis, bedienzeit, neuerPreis } = inputs;
  const anpassung = useMemo(
    () => (bisherigerPreis > 0 ? (neuerPreis / bisherigerPreis - 1) * 100 : 0),
    [bisherigerPreis, neuerPreis]
  );

  const info = useMemo(() => {
    if (anpassung >= 25)
      return { class: 'anpassung-critical', text: 'Anpassung viel zu hoch!', icon: '🚨' };
    if (anpassung > 15)
      return { class: 'anpassung-moderate', text: 'Anpassung ist in Ordnung.', icon: '↗️' };
    return { class: 'anpassung-ok', text: 'Anpassung ist gut.', icon: '↗️' };
  }, [anpassung]);

  const nudgeAnpassung = (dir: -1 | 1) => {
    if (!(bisherigerPreis > 0)) return;
    onManualNeuerPreis?.();
    const stepPct = 1;
    const curPct = anpassung;
    const rem = ((curPct % stepPct) + stepPct) % stepPct;
    const isAligned = rem < 0.05 || stepPct - rem < 0.05;
    const targetPct = !isAligned
      ? dir > 0
        ? Math.ceil(curPct)
        : Math.floor(curPct)
      : Math.round(curPct) + dir * stepPct;
    const nextNeuerPreis = Math.max(
      0,
      Number((bisherigerPreis * (1 + targetPct / 100)).toFixed(2))
    );
    setInputs((p: any) => ({ ...p, neuerPreis: nextNeuerPreis }));
  };

  return (
    <div className="grid grid-cols-4 gap-1.5 sm:gap-4 items-start mb-2 sm:mb-4 pb-2 sm:pb-4 border-b border-pe-border dark:border-[#222]">
      <h3 className="col-span-4 text-sm sm:text-2xl font-bold border-b border-pe-border dark:border-[#222] pb-1 sm:pb-3 mb-1 sm:mb-2 text-customBeige">
        Kalkulator
      </h3>

      <div className="flex flex-col text-center cursor-help min-w-0">
        <label className="flex items-end justify-center h-5 sm:h-6 text-[9px] sm:text-xs font-bold text-stone-500 dark:text-gray-500 uppercase tracking-wide sm:tracking-widest mb-0.5 sm:mb-1 leading-tight">
          <span className="sm:hidden">Bisher</span>
          <span className="hidden sm:inline">bisher. Preis</span>
        </label>
        <SteppedEditableField
          value={bisherigerPreis}
          onSave={(val) => setInputs((p: any) => ({ ...p, bisherigerPreis: val }))}
          step={5}
          format="currency"
          formatCurrency={formatCurrency}
          className="w-full !py-1 sm:!py-2 !text-xs sm:!text-base !px-0.5 sm:!px-1"
          ariaLabel="Bisheriger Preis"
          compact
        />
        <div className="hidden sm:block h-4" aria-hidden="true" />
      </div>
      <div className="flex flex-col text-center cursor-help min-w-0">
        <label className="flex items-end justify-center h-5 sm:h-6 text-[9px] sm:text-xs font-bold text-stone-500 dark:text-gray-500 uppercase tracking-wide sm:tracking-widest mb-0.5 sm:mb-1 leading-tight">
          <span className="sm:hidden">Zeit</span>
          <span className="hidden sm:inline">Bedienzeit</span>
        </label>
        <SteppedEditableField
          value={bedienzeit}
          onSave={(val) => {
            if (onChangeBedienzeit) {
              onChangeBedienzeit(val);
            } else {
              setInputs((p: any) => ({ ...p, bedienzeit: val }));
            }
          }}
          step={5}
          format="minutes"
          className="w-full !py-1 sm:!py-2 !text-xs sm:!text-base !px-0.5 sm:!px-1"
          ariaLabel="Bedienzeit"
          compact
        />
        <div className="hidden sm:block h-4" aria-hidden="true" />
      </div>
      <div className="flex flex-col text-center cursor-help min-w-0">
        <label className="flex items-end justify-center h-5 sm:h-6 text-[9px] sm:text-xs font-bold text-stone-500 dark:text-gray-500 uppercase tracking-wide sm:tracking-widest mb-0.5 sm:mb-1 leading-tight">
          <span className="sm:hidden">Neu</span>
          <span className="hidden sm:inline">neuer Preis</span>
        </label>
        <SteppedEditableField
          value={neuerPreis}
          onSave={(val) => {
            onManualNeuerPreis?.();
            setInputs((p: any) => ({ ...p, neuerPreis: val }));
          }}
          step={5}
          format="currency"
          formatCurrency={formatCurrency}
          className="w-full !py-1 sm:!py-2 !text-xs sm:!text-base !px-0.5 sm:!px-1"
          ariaLabel="Neuer Preis"
          compact
        />
        <div className="hidden sm:block h-4" aria-hidden="true" />
      </div>
      <div className="flex flex-col text-center cursor-help min-w-0">
        <label className="flex items-end justify-center h-5 sm:h-6 text-[9px] sm:text-xs font-bold text-stone-500 dark:text-gray-500 uppercase tracking-wide sm:tracking-widest mb-0.5 sm:mb-1 leading-tight">
          Anpassung
        </label>
        <div className="step-field-wrap step-field-compact">
          <button
            type="button"
            className="step-btn"
            aria-label="Anpassung verringern"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              nudgeAnpassung(-1);
            }}
          >
            −
          </button>
          <div className={`flex-1 min-w-0 flex items-center justify-center font-semibold text-center text-sm sm:text-xl font-numeric whitespace-nowrap leading-none ${info.class}`}>
            <span>{formatPercent(anpassung)}</span>
            <span className="ml-1 text-[0.85em] leading-none" aria-hidden="true">{info.icon}</span>
          </div>
          <button
            type="button"
            className="step-btn"
            aria-label="Anpassung erhöhen"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              nudgeAnpassung(1);
            }}
          >
            +
          </button>
        </div>
        <div className="hidden sm:block text-[10px] sm:text-xs text-pe-muted dark:text-gray-400 h-4 leading-4 truncate">
          {info.text}
        </div>
      </div>
    </div>
  );
};

const SoloGewinnGauge = ({
  gewinn,
  gewinnPercent,
  targetPercent = 12.5,
  formatCurrency,
}: {
  gewinn: number;
  gewinnPercent: number;
  targetPercent?: number;
  formatCurrency: (v: number, d?: number) => string;
}) => {
  const gaugeSettings = useMemo(() => {
    if (gewinnPercent >= targetPercent * 1.6)
      return {
        color: '#8b5cf6',
        textColor: 'text-exzellent',
        text: 'Hervorragendes Ergebnis',
        iconType: 'star',
        highlightColor: '#a78bfa',
      };
    if (gewinnPercent >= targetPercent * 1.2)
      return {
        color: '#3b82f6',
        textColor: 'text-gut',
        text: 'Gesunde Rücklagen',
        iconType: 'double_check',
        highlightColor: '#60a5fa',
      };
    if (gewinnPercent >= targetPercent)
      return {
        color: '#16a34a',
        textColor: 'text-gewinn',
        text: 'Solider Gewinn',
        iconType: 'check',
        highlightColor: '#4ade80',
      };
    if (gewinnPercent >= 0)
      return {
        color: '#ca8a04',
        textColor: 'text-kostendeckend',
        text: 'Kostendeckend',
        iconType: 'dash',
        highlightColor: '#facc15',
      };
    return {
      color: '#dc2626',
      textColor: 'text-verlust',
      text: 'Verlust',
      iconType: 'cross',
      highlightColor: '#f87171',
    };
  }, [gewinnPercent, targetPercent]);

  const radius = 40;
  const circumference = 2 * Math.PI * radius;
  const maxScale = Math.max(20, targetPercent * 1.6);
  const visualPercent = Math.min(Math.max(0, gewinnPercent), maxScale) / maxScale;
  const offset = circumference * (1 - visualPercent);

  const renderIcon = () => {
    const shadow = 'drop-shadow(1px 2px 2px rgba(0,0,0,0.5))';
    const stroke = 'white';
    const strokeWidth = '10';
    switch (gaugeSettings.iconType) {
      case 'cross':
        return (
          <path
            d="M30 30 L70 70 M70 30 L30 70"
            stroke={stroke}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            filter={shadow}
          />
        );
      case 'dash':
        return (
          <path
            d="M25 50 H75"
            stroke={stroke}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            filter={shadow}
          />
        );
      case 'check':
        return (
          <path
            d="M30 50 L45 65 L75 35"
            fill="none"
            stroke={stroke}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeLinejoin="round"
            filter={shadow}
          />
        );
      case 'double_check':
        return (
          <g>
            <path
              d="M20 50 L35 65 L65 35"
              fill="none"
              stroke={stroke}
              strokeWidth="8"
              strokeLinecap="round"
              strokeLinejoin="round"
              filter={shadow}
              opacity="0.6"
              transform="translate(-5, 0)"
            />
            <path
              d="M35 50 L50 65 L80 35"
              fill="none"
              stroke={stroke}
              strokeWidth="8"
              strokeLinecap="round"
              strokeLinejoin="round"
              filter={shadow}
            />
          </g>
        );
      case 'star':
        return (
          <path
            d="M50 15 L61 39 H88 L66 57 L75 85 L50 69 L25 85 L34 57 L12 39 H39 Z"
            fill={stroke}
            filter={shadow}
            transform="scale(0.8) translate(12, 12)"
          />
        );
      default:
        return null;
    }
  };

  return (
    <div
      className="mt-2 sm:mt-4 p-2 sm:p-3 gauge-container-3d flex flex-row items-center justify-around gap-2 sm:gap-4"
      style={{
        borderWidth: '2px',
        borderColor: gaugeSettings.highlightColor,
        boxShadow: `0 0 20px ${gaugeSettings.highlightColor}40, inset 0 0 30px rgba(0,0,0,0.8)`,
      }}
    >
      <div className="relative w-14 h-14 sm:w-28 sm:h-28 flex-shrink-0">
        <svg className="w-full h-full drop-shadow-2xl" viewBox="0 0 100 100">
          <defs>
            <filter id="solo-glow" x="-50%" y="-50%" width="200%" height="200%">
              <feGaussianBlur stdDeviation="2.5" result="coloredBlur" />
              <feMerge>
                <feMergeNode in="coloredBlur" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
            <filter id="solo-button3D" x="-50%" y="-50%" width="200%" height="200%">
              <feDropShadow dx="0" dy="5" stdDeviation="4" floodColor="rgba(0,0,0,0.6)" />
            </filter>
            <linearGradient id="solo-gaugeGradient" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor={gaugeSettings.color} stopOpacity="1" />
              <stop offset="100%" stopColor="#ffffff" stopOpacity="0.5" />
            </linearGradient>
          </defs>
          <circle
            cx="50"
            cy="50"
            r={radius}
            fill="none"
            stroke="#0f172a"
            strokeWidth="8"
            style={{ filter: 'drop-shadow(0 1px 1px rgba(255,255,255,0.1))' }}
          />
          <circle
            id="solo-gewinn-gauge-progress"
            cx="50"
            cy="50"
            r={radius}
            fill="none"
            stroke="url(#solo-gaugeGradient)"
            strokeWidth="8"
            strokeLinecap="round"
            style={{
              strokeDasharray: circumference,
              strokeDashoffset: offset,
              transform: 'rotate(-90deg)',
              transformOrigin: '50% 50%',
              filter: 'url(#solo-glow)',
            }}
          />
          <g transform="translate(28, 28) scale(0.44)">
            <rect
              x="10"
              y="10"
              width="80"
              height="80"
              rx="25"
              fill={gaugeSettings.color}
              style={{ filter: 'url(#solo-button3D)' }}
            />
            {renderIcon()}
            <path
              d="M20 20 Q 50 5, 80 20 Q 70 40, 50 35 Q 30 40, 20 20"
              fill="white"
              opacity="0.15"
            />
          </g>
        </svg>
      </div>
      <div className="text-center space-y-0 sm:space-y-1">
        <div className="text-[8px] sm:text-xs text-pe-muted dark:text-gray-400 uppercase tracking-wider">
          Gewinn
        </div>
        <div
          className={`text-base sm:text-3xl font-bold font-numeric ${gaugeSettings.textColor}`}
          style={{ textShadow: '0 2px 4px rgba(0,0,0,0.5)' }}
        >
          {symbolToText(formatCurrency(gewinn))}
        </div>
      </div>
      <div className="text-center space-y-0 sm:space-y-1">
        <div className="text-[8px] sm:text-xs text-pe-muted dark:text-gray-400 uppercase tracking-wider">
          Rücklage
        </div>
        <div
          className={`text-base sm:text-3xl font-semibold font-numeric ${gaugeSettings.textColor}`}
          style={{ textShadow: '0 2px 4px rgba(0,0,0,0.5)' }}
        >
          {formatPercent(gewinnPercent)}
        </div>
      </div>
      <div className="text-center space-y-0 sm:space-y-1 min-w-0">
        <div className="text-[8px] sm:text-xs text-pe-muted dark:text-gray-400 uppercase tracking-wider">
          Status
        </div>
        <div
          className={`text-[11px] sm:text-lg font-bold leading-tight ${gaugeSettings.textColor}`}
          style={{ textShadow: '0 0 10px currentColor' }}
        >
          {gaugeSettings.text}
        </div>
      </div>
    </div>
  );
};

const SoloHauptKalkulator = ({
  materialData,
  geraeteListe,
  gemeinkostenfaktor,
  prodStundenlohnChef,
  mwstSatz,
  verlustPercent,
  lohnfortzahlungPercent,
  matFaktor,
  liquiditaetPercent,
  formatCurrency,
  currencyCode,
  barometerStundenfaktor,
}: {
  materialData: MatItem[];
  geraeteListe: DevItem[];
  gemeinkostenfaktor: number;
  prodStundenlohnChef: number;
  mwstSatz: number;
  verlustPercent: number;
  lohnfortzahlungPercent: number;
  matFaktor: number;
  liquiditaetPercent: number;
  formatCurrency: (v: number, d?: number) => string;
  currencyCode: string;
  /** Barometer „Stundensatz ohne Material“ aus KalkulatorPage */
  barometerStundenfaktor: number;
}) => {
  const [inputs, setInputs] = useState({
    bisherigerPreis: 0,
    neuerPreis: 0,
    bedienzeit: 60,
    materialPreis: 0,
    materialGramm: 0,
  });
  const [activeMaterialIDs, setActiveMaterialIDs] = useState<Set<string>>(() => new Set());
  const [activeDeviceIDs, setActiveDeviceIDs] = useState<Set<string>>(() => new Set());
  const [matOpenMobile, setMatOpenMobile] = useState(false);
  const barometerSeededRef = useRef(false);
  /** true = User hat „neuer Preis“ manuell gesetzt → kein Auto-Break-even mehr */
  const neuerPreisManualRef = useRef(false);

  // Auto-Übernahme: Barometer → nur bisheriger Preis (Vergleichsbasis)
  useEffect(() => {
    const rate = Number(barometerStundenfaktor) || 0;
    if (rate <= 0) return;
    setInputs((p) => {
      if (barometerSeededRef.current && p.bisherigerPreis > 0) {
        return p;
      }
      barometerSeededRef.current = true;
      return {
        ...p,
        bisherigerPreis: p.bisherigerPreis > 0 ? p.bisherigerPreis : rate,
        bedienzeit: p.bedienzeit > 0 ? p.bedienzeit : 60,
      };
    });
  }, [barometerStundenfaktor]);

  // Neuer Preis: automatisch Kostendeckung (Gewinn ≈ 0), bis der User manuell eingreift
  const breakEvenBrutto = useMemo(
    () =>
      calcSoloBreakEvenBrutto({
        bedienzeit: inputs.bedienzeit,
        materialPreis: inputs.materialPreis,
        materialGramm: inputs.materialGramm,
        activeMaterialIDs,
        materialData,
        activeDeviceIDs,
        geraeteListe,
        mwstSatz,
        gemeinkostenfaktor,
        verlustPercent,
        matFaktor,
        prodStundenlohnChef,
        lohnfortzahlungPercent,
      }),
    [
      inputs.bedienzeit,
      inputs.materialPreis,
      inputs.materialGramm,
      activeMaterialIDs,
      materialData,
      activeDeviceIDs,
      geraeteListe,
      mwstSatz,
      gemeinkostenfaktor,
      verlustPercent,
      matFaktor,
      prodStundenlohnChef,
      lohnfortzahlungPercent,
    ]
  );

  useEffect(() => {
    if (neuerPreisManualRef.current) return;
    if (!(breakEvenBrutto > 0)) return;
    setInputs((p) => {
      if (Math.abs((Number(p.neuerPreis) || 0) - breakEvenBrutto) < 0.01) return p;
      return { ...p, neuerPreis: breakEvenBrutto };
    });
  }, [breakEvenBrutto]);

  const toggleMaterial = (id: string) => {
    setActiveMaterialIDs((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleDevice = (id: string) => {
    setActiveDeviceIDs((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const hasDevicesInStructure = geraeteListe.length > 0;
  const devicesCurrentlyChecked = activeDeviceIDs.size > 0;
  const activeMatCount = activeMaterialIDs.size;
  const activeDevCount = activeDeviceIDs.size;
  const fmtEUR = (val: number) => symbolToText(formatCurrency(val));

  // Solo: unpr. Unternehmerlohn = 0 (Chef-Kosten laufen über Minutensatz, kein Doppelabzug)
  const unprodUnternehmerlohnPercent = 0;

  const calculations = useMemo(() => {
    const { bisherigerPreis, neuerPreis, bedienzeit, materialPreis, materialGramm } = inputs;
    const mwst = (mwstSatz || 0) / 100;
    const gk = gemeinkostenfaktor || 0;
    const ul = unprodUnternehmerlohnPercent / 100;
    const vl = (verlustPercent || 0) / 100;

    let mSum = 0;
    let mCnt = 0;
    activeMaterialIDs.forEach((id) => {
      const m = materialData.find((x) => x.id === id);
      if (m && m.gebinde > 0) {
        mSum += (m.ekPreis / m.gebinde) * 100;
        mCnt++;
      }
    });
    const avg100 = mCnt > 0 ? mSum / mCnt : 0;
    const matCost =
      ((avg100 / 100) * materialGramm + materialPreis) * (matFaktor > 0 ? matFaktor : 1.3);

    const mwstVal = neuerPreis - neuerPreis / (1 + mwst);
    const netto = neuerPreis - mwstVal;

    let dPerc = 0;
    activeDeviceIDs.forEach((id) => {
      const d = geraeteListe.find((x) => x.id === id);
      if (d) dPerc += d.kostenProzent;
    });
    const devCostTotal = netto * (dPerc / 100);

    const uebrig = Number(
      (netto - matCost - devCostTotal - netto * gk - netto * ul - netto * vl).toFixed(2)
    );

    // Solo-Chef: Minutensatz inkl. Urlaubsausgleich (kein Team-SV, Solo = 0%)
    const lohnBasis = Number((prodStundenlohnChef * (bedienzeit / 60)).toFixed(2));
    const urlaubVal = Number((lohnBasis * (lohnfortzahlungPercent / 100)).toFixed(2));
    const totalLohn = Number((lohnBasis + urlaubVal).toFixed(2));
    const finalGewinn = Number((uebrig - totalLohn).toFixed(2));

    return {
      mwstValue: mwstVal,
      nettoNachMwst: netto,
      totalMaterialCost: matCost,
      totalActivatedDevicePrice: devCostTotal,
      totalActivatedDevicePricePercent: dPerc,
      gemeinkostenValue: netto * gk,
      unternehmerlohnValue: netto * ul,
      verlustValue: netto * vl,
      uebrigFuerLohn: uebrig,
      lohnkostenValue: totalLohn,
      gewinn: finalGewinn,
      gewinnPercent: netto > 0 ? (finalGewinn / netto) * 100 : 0,
      totalMaterialCostPercent: netto > 0 ? (matCost / netto) * 100 : 0,
      currentAnpassung:
        bisherigerPreis > 0 ? (neuerPreis / bisherigerPreis - 1) * 100 : 0,
    };
  }, [
    inputs,
    materialData,
    activeMaterialIDs,
    geraeteListe,
    activeDeviceIDs,
    mwstSatz,
    gemeinkostenfaktor,
    verlustPercent,
    matFaktor,
    prodStundenlohnChef,
    lohnfortzahlungPercent,
  ]);

  return (
    <div className="flex flex-col xl:flex-row items-start gap-3 xl:gap-5 2xl:gap-8">
      {/* Mobil: Material/Geräte Klappe */}
      <div className="xl:hidden w-full order-1">
        <div className="carbon-panel carbon-weave-bg w-full">
          <div className="carbon-content px-3 py-2">
            <button
              type="button"
              onClick={() => setMatOpenMobile((v) => !v)}
              className="w-full flex items-center justify-between gap-2 text-left"
            >
              <div className="min-w-0">
                <span className="text-sm font-bold text-customBeige">Material / Geräte</span>
                <span className="ml-2 text-[10px] font-mono text-stone-500 dark:text-gray-500">
                  {activeMatCount} Mat{hasDevicesInStructure ? ` · ${activeDevCount} Ger` : ''}
                </span>
              </div>
              <span className={`text-customBeige text-xs transition-transform ${matOpenMobile ? 'rotate-180' : ''}`}>
                ▼
              </span>
            </button>
            {matOpenMobile && (
              <div className="mt-2 pt-2 border-t border-pe-border dark:border-[#222]">
                <div className="flex justify-end mb-1">
                  <span className="text-[9px] font-black uppercase tracking-widest text-stone-500 dark:text-slate-500">
                    Preis pro 100&nbsp;ml/g
                  </span>
                </div>
                <div className="space-y-1.5 max-h-40 overflow-y-auto">
                  {(materialData || []).map((m) => (
                    <div key={m.id} className="flex items-center justify-between text-xs gap-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <input
                          type="checkbox"
                          id={`solo-activator-mobile-${m.id}`}
                          checked={activeMaterialIDs.has(m.id)}
                          onChange={() => toggleMaterial(m.id)}
                        />
                        <label
                          htmlFor={`solo-activator-mobile-${m.id}`}
                          className="text-pe-muted dark:text-gray-300 truncate"
                        >
                          {m.name}
                        </label>
                      </div>
                      <span className="text-customBeige font-semibold font-numeric whitespace-nowrap shrink-0">
                        {fmtEUR(m.gebinde > 0 ? (m.ekPreis / m.gebinde) * 100 : 0)}
                        <span className="text-[9px] font-bold text-stone-500 dark:text-slate-500 ml-1">
                          /100
                        </span>
                      </span>
                    </div>
                  ))}
                </div>
                {hasDevicesInStructure && (
                  <div className="mt-3 pt-2 border-t border-pe-border dark:border-[#222]">
                    <h4 className="text-[10px] font-bold text-customBeige uppercase tracking-wider mb-1.5">
                      Geräte
                    </h4>
                    <div className="space-y-1.5">
                      {geraeteListe.map((d) => (
                        <div key={d.id} className="flex items-center justify-between text-xs gap-2">
                          <div className="flex items-center gap-2 min-w-0">
                            <input
                              type="checkbox"
                              id={`solo-device-mobile-${d.id}`}
                              checked={activeDeviceIDs.has(d.id)}
                              onChange={() => toggleDevice(d.id)}
                            />
                            <label
                              htmlFor={`solo-device-mobile-${d.id}`}
                              className="text-pe-muted dark:text-gray-300 truncate"
                            >
                              {d.name}
                            </label>
                          </div>
                          <span className="text-customBeige font-numeric shrink-0">
                            {d.kostenProzent.toLocaleString('de-DE', {
                              minimumFractionDigits: 1,
                              maximumFractionDigits: 1,
                            })}
                            %
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* LINKS: Kaskade — bewusst breiter */}
      <div className="flex-grow flex flex-col w-full order-2 xl:order-1 min-w-0 xl:min-w-[640px]">
        <section className="carbon-panel carbon-weave-bg w-full">
          <div className="carbon-content p-3 sm:p-5">
            <SoloKalkulatorInputs
              inputs={inputs}
              setInputs={setInputs}
              formatCurrency={formatCurrency}
              onManualNeuerPreis={() => {
                neuerPreisManualRef.current = true;
              }}
              onChangeBedienzeit={(nextMinsRaw: number) => {
                const nextMins = Math.max(5, Number(nextMinsRaw) || 60);
                const prevMins = Math.max(5, Number(inputs.bedienzeit) || 60);
                neuerPreisManualRef.current = false;
                const baseRate60 = Number(barometerStundenfaktor) || 0;
                const scaledBisher =
                  baseRate60 > 0
                    ? Number(((baseRate60 / 60) * nextMins).toFixed(2))
                    : inputs.bisherigerPreis > 0
                      ? Number(((inputs.bisherigerPreis / prevMins) * nextMins).toFixed(2))
                      : 0;
                setInputs((p: any) => ({
                  ...p,
                  bedienzeit: nextMins,
                  bisherigerPreis: scaledBisher > 0 ? scaledBisher : p.bisherigerPreis,
                }));
              }}
            />

            <div className="flex flex-col sm:flex-row items-stretch gap-2 sm:gap-4">
              <div className="flex-grow w-full flex flex-col min-w-0">
                <div className="grid grid-cols-[minmax(0,1fr)_auto_auto] sm:grid-cols-[minmax(0,2.2fr)_0.7fr_1fr_1.1fr] gap-x-2 sm:gap-x-4 gap-y-1.5 sm:gap-y-3 items-start text-sm sm:text-base leading-snug">
                  <div className="text-pe-muted dark:text-slate-300 py-0">abzügl. MwSt.</div>
                  <div className="text-right sm:text-center text-[11px] sm:text-base text-stone-500 dark:text-gray-500 font-numeric py-0 tabular-nums">
                    {formatPercent(Number(mwstSatz.toFixed(2)), 1)}
                  </div>
                  <div className="text-right text-customBeige font-semibold font-numeric py-0">
                    {fmtEUR(calculations.mwstValue)}
                  </div>
                  <div className="hidden sm:block text-right font-bold text-customBeige font-numeric py-0">
                    {fmtEUR(calculations.nettoNachMwst)}
                  </div>

                  {hasDevicesInStructure && devicesCurrentlyChecked && (
                    <>
                      <div className="text-pe-muted dark:text-slate-300 py-0">abzügl. Geräte</div>
                      <div className="text-right sm:text-center text-[11px] sm:text-base text-stone-500 dark:text-gray-500 font-numeric py-0 tabular-nums">
                        {formatPercent(calculations.totalActivatedDevicePricePercent)}
                      </div>
                      <div className="text-right text-customBeige font-semibold font-numeric py-0">
                        {fmtEUR(calculations.totalActivatedDevicePrice)}
                      </div>
                      <div className="hidden sm:block" />
                    </>
                  )}

                  <div className="text-pe-muted dark:text-slate-300 py-0">abzügl. Material</div>
                  <div className="text-right sm:text-center text-[11px] sm:text-base text-stone-500 dark:text-gray-500 font-numeric py-0 tabular-nums">
                    {formatPercent(calculations.totalMaterialCostPercent)}
                  </div>
                  <div className="text-right text-customBeige font-semibold font-numeric py-0">
                    {fmtEUR(calculations.totalMaterialCost)}
                  </div>
                  <div className="hidden sm:block" />

                  <div className="text-pe-muted dark:text-slate-300 py-0">abzügl. Gemeinkosten</div>
                  <div className="text-right sm:text-center text-[11px] sm:text-base text-stone-500 dark:text-gray-500 font-numeric py-0 tabular-nums">
                    {formatPercent((gemeinkostenfaktor || 0) * 100)}
                  </div>
                  <div className="text-right text-customBeige font-semibold font-numeric py-0">
                    {fmtEUR(calculations.gemeinkostenValue)}
                  </div>
                  <div className="hidden sm:block" />

                  <div className="text-pe-muted dark:text-slate-300 py-0">
                    <span className="sm:hidden">abzügl. Unternehmerlohn</span>
                    <span className="hidden sm:inline">abzügl. unpr. Unternehmerlohn</span>
                  </div>
                  <div className="text-right sm:text-center text-[11px] sm:text-base text-stone-500 dark:text-gray-500 font-numeric py-0 tabular-nums">
                    {formatPercent(unprodUnternehmerlohnPercent)}
                  </div>
                  <div className="text-right text-customBeige font-semibold font-numeric py-0">
                    {fmtEUR(calculations.unternehmerlohnValue)}
                  </div>
                  <div className="hidden sm:block" />

                  <div className="text-pe-muted dark:text-slate-300 py-0">abzügl. kalk. Verlust</div>
                  <div className="text-right sm:text-center text-[11px] sm:text-base text-stone-500 dark:text-gray-500 font-numeric py-0 tabular-nums">
                    {formatPercent(verlustPercent || 0)}
                  </div>
                  <div className="text-right text-customBeige font-semibold font-numeric py-0">
                    {fmtEUR(calculations.verlustValue)}
                  </div>
                  <div className="hidden sm:block" />

                  <div className="col-span-3 sm:col-span-4 border-b border-pe-border dark:border-[#222] my-0.5 sm:my-1" />

                  <div className="font-bold text-sm sm:text-lg py-0 text-pe-text col-span-2 sm:col-span-1">
                    übrig für Lohnkosten
                  </div>
                  <div className="text-right font-bold text-sm sm:text-lg text-customBeige font-numeric sm:col-start-4 py-0 whitespace-nowrap">
                    {fmtEUR(calculations.uebrigFuerLohn)}
                  </div>

                  <div className="col-span-3 sm:col-span-4 mt-0.5 sm:mt-1 p-2 sm:p-3 bg-pe-sunken rounded-lg sm:rounded-xl border border-pe-border dark:border-[#222] shadow-[inset_0_2px_10px_rgba(0,0,0,0.5)]">
                    <div className="grid grid-cols-[minmax(0,1fr)_auto] sm:grid-cols-[2fr_0.8fr_1fr_1fr] gap-x-3 sm:gap-x-4 items-center">
                      <div className="text-pe-muted dark:text-slate-300 font-bold text-sm sm:text-base leading-tight">
                        abzügl. Lohn &amp; Nebenkosten
                      </div>
                      <div className="text-right text-customBeige font-bold text-base sm:text-xl font-numeric sm:col-start-4 whitespace-nowrap">
                        {fmtEUR(calculations.lohnkostenValue)}
                      </div>
                      <div className="col-span-2 sm:col-start-3 sm:col-span-2 text-right text-[10px] sm:text-xs font-mono text-stone-500 dark:text-gray-500 mt-0.5 sm:mt-2 uppercase tracking-tight font-bold whitespace-nowrap">
                        Solo-Chef {fmtEUR(prodStundenlohnChef)}/Std | Url.{' '}
                        {lohnfortzahlungPercent.toLocaleString('de-DE', {
                          minimumFractionDigits: 1,
                          maximumFractionDigits: 1,
                        })}
                        %
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Desktop: Mat. EUR / Mat. g / Chef Lohn — etwas schmaler */}
              <div className="hidden sm:flex w-[220px] flex-shrink-0 flex-col gap-2.5 justify-between">
                <div className="bg-pe-tile-header rounded-xl p-3 border border-pe-border dark:border-[#222] shadow-[inset_0_2px_10px_rgba(0,0,0,0.5)] text-center">
                  <h4 className="text-xs font-bold text-stone-500 dark:text-gray-500 uppercase tracking-widest mb-1.5">
                    Mat. {currencyCode}
                  </h4>
                  <SteppedEditableField
                    value={inputs.materialPreis}
                    onSave={(v) => setInputs((p: any) => ({ ...p, materialPreis: v }))}
                    step={5}
                    format="currency"
                    formatCurrency={formatCurrency}
                    className="w-full !text-lg !font-bold !py-1 !px-1"
                    ariaLabel="Materialpreis"
                  />
                </div>
                <div className="bg-pe-card rounded-xl p-3 border border-customBeige/50 shadow-[inset_0_2px_10px_rgba(0,0,0,0.5)] text-center">
                  <h4 className="text-xs font-bold text-customBeige uppercase tracking-widest mb-1.5">
                    Mat. g
                  </h4>
                  <SteppedEditableField
                    value={inputs.materialGramm}
                    onSave={(v) => setInputs((p: any) => ({ ...p, materialGramm: v }))}
                    step={5}
                    format="gramm"
                    className="w-full !text-lg !font-bold !py-1 !px-1"
                    ariaLabel="Materialmenge"
                  />
                </div>
                <div className="bg-pe-tile-header rounded-xl p-3 border border-pe-border dark:border-[#222] shadow-[inset_0_2px_10px_rgba(0,0,0,0.5)] text-center mt-auto">
                  <h4 className="text-xs font-bold text-stone-500 dark:text-gray-500 uppercase tracking-widest mb-1.5">
                    Chef Lohn
                  </h4>
                  <div className="calculated-field w-full !text-lg !font-bold !py-0.5">
                    {fmtEUR(prodStundenlohnChef)}
                  </div>
                </div>
              </div>
            </div>

            {/* Mobil: Mat + Chef */}
            <div className="sm:hidden grid grid-cols-3 gap-2 mt-3">
              <div className="bg-pe-tile-header rounded-lg p-2 border border-pe-border dark:border-[#222] shadow-[inset_0_2px_10px_rgba(0,0,0,0.5)] text-center">
                <h4 className="text-[9px] font-bold text-stone-500 dark:text-gray-500 uppercase tracking-widest mb-1">
                  Mat. {currencyCode}
                </h4>
                <SteppedEditableField
                  value={inputs.materialPreis}
                  onSave={(v) => setInputs((p: any) => ({ ...p, materialPreis: v }))}
                  step={5}
                  format="currency"
                  formatCurrency={formatCurrency}
                  className="w-full !text-xs !font-bold !py-1 !px-0.5"
                  ariaLabel="Materialpreis"
                  compact
                />
              </div>
              <div className="bg-pe-card rounded-lg p-2 border border-customBeige/50 shadow-[inset_0_2px_10px_rgba(0,0,0,0.5)] text-center">
                <h4 className="text-[9px] font-bold text-customBeige uppercase tracking-widest mb-1">
                  Mat. g
                </h4>
                <SteppedEditableField
                  value={inputs.materialGramm}
                  onSave={(v) => setInputs((p: any) => ({ ...p, materialGramm: v }))}
                  step={5}
                  format="gramm"
                  className="w-full !text-xs !font-bold !py-1 !px-0.5"
                  ariaLabel="Materialmenge"
                  compact
                />
              </div>
              <div className="bg-pe-tile-header rounded-lg p-2 border border-pe-border dark:border-[#222] shadow-[inset_0_2px_10px_rgba(0,0,0,0.5)] text-center">
                <h4 className="text-[9px] font-bold text-stone-500 dark:text-gray-500 uppercase tracking-widest mb-1">
                  Chef Lohn
                </h4>
                <div className="calculated-field w-full !text-sm !font-bold !py-1">
                  {fmtEUR(prodStundenlohnChef)}
                </div>
              </div>
            </div>

            <div className="flex flex-row flex-wrap justify-end items-center mt-2 sm:mt-5 gap-2 sm:gap-3">
              <div className="flex items-baseline justify-end whitespace-nowrap text-right">
                <span className="text-[11px] sm:text-base font-bold text-pe-text">
                  Ziel min. {formatPercent(liquiditaetPercent || 0, 0)}
                  <span className="hidden sm:inline text-[10px] sm:text-xs text-pe-muted dark:text-gray-400 sm:ml-4 font-normal">
                    {' '}
                    für Rücklagen und Liquidität
                  </span>
                </span>
              </div>
            </div>

            <SoloGewinnGauge
              gewinn={calculations.gewinn}
              gewinnPercent={calculations.gewinnPercent}
              targetPercent={liquiditaetPercent || 12.5}
              formatCurrency={formatCurrency}
            />
          </div>
        </section>
      </div>

      {/* RECHTS: Aktivierungs-Sidebar — schmaler, mehr Luft für Kalkulator */}
      <div className="hidden xl:flex flex-shrink-0 flex-col w-full xl:w-72 2xl:w-80 order-3 xl:order-2">
        <div className="carbon-panel carbon-weave-bg w-full">
          <div className="carbon-content p-4 2xl:p-5">
            <div className="flex justify-between items-end mb-4 border-b border-pe-border dark:border-[#222] pb-3 gap-2">
              <h3 className="text-base font-bold text-customBeige uppercase tracking-wide leading-tight">
                Material aktivieren
              </h3>
              <span className="text-[9px] font-black uppercase tracking-widest text-stone-500 dark:text-slate-500 shrink-0">
                /100&nbsp;ml·g
              </span>
            </div>
            <div className="space-y-3">
              {(materialData || []).length === 0 ? (
                <p className="text-xs text-pe-muted dark:text-gray-500">
                  Kein Materialkatalog in den Settings hinterlegt.
                </p>
              ) : (
                materialData.map((m) => (
                  <div key={m.id} className="flex items-center justify-between text-sm gap-2">
                    <div className="flex items-center gap-3 min-w-0">
                      <input
                        type="checkbox"
                        id={`solo-activator-${m.id}`}
                        checked={activeMaterialIDs.has(m.id)}
                        onChange={() => toggleMaterial(m.id)}
                      />
                      <label
                        htmlFor={`solo-activator-${m.id}`}
                        className="text-pe-muted dark:text-gray-300 truncate"
                      >
                        {m.name}
                      </label>
                    </div>
                    <span className="text-customBeige font-semibold font-numeric whitespace-nowrap shrink-0">
                      {fmtEUR(m.gebinde > 0 ? (m.ekPreis / m.gebinde) * 100 : 0)}
                      <span className="text-[9px] font-bold text-stone-500 dark:text-slate-500 ml-1">
                        /100
                      </span>
                    </span>
                  </div>
                ))
              )}
            </div>
            {hasDevicesInStructure && (
              <div className="mt-8 pt-6 border-t border-pe-border dark:border-[#222]">
                <h3 className="text-lg font-bold mb-4 text-customBeige uppercase tracking-wide">
                  Geräte aktivieren
                </h3>
                <div className="space-y-3">
                  {geraeteListe.map((d) => (
                    <div key={d.id} className="flex items-center justify-between text-sm">
                      <div className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          id={`solo-device-${d.id}`}
                          checked={activeDeviceIDs.has(d.id)}
                          onChange={() => toggleDevice(d.id)}
                        />
                        <label
                          htmlFor={`solo-device-${d.id}`}
                          className="text-pe-muted dark:text-gray-300"
                        >
                          {d.name}
                        </label>
                      </div>
                      <span className="text-customBeige font-numeric">
                        Faktor:{' '}
                        {d.kostenProzent.toLocaleString('de-DE', {
                          minimumFractionDigits: 1,
                          maximumFractionDigits: 1,
                        })}
                        %
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

/* ============================================================================
 * ZONE 1 — Stellschrauben (System-Design pe-Tokens)
 * ========================================================================== */

/** Detail-Auflistung wie Lohnfaktor — ohne Passwort (Admin-only Tool) */
const SoloKostenDetails = ({
  snapshot,
  derived,
  knobs,
  currencyCode,
  formatCurrency,
}: {
  snapshot: SoloSimulationSnapshot;
  derived: SoloSimulationDerived;
  knobs: SoloSimulationKnobs;
  currencyCode: string;
  formatCurrency: (v: number, d?: number) => string;
}) => {
  const fmt = (v: number) => formatCurrency(v, 0);
  const fmt2 = (v: number) => formatCurrency(v, 2);

  const SectionHeader = ({ title }: { title: string }) => (
    <div className="bg-pe-tile-header dark:bg-slate-700 text-pe-text font-bold text-center py-1.5 px-4 rounded-md my-3 border border-pe-border-strong dark:border-slate-600 text-sm">
      {title}
    </div>
  );

  const CostRow = ({
    label,
    value,
    prevValue,
    hint,
    decimals = 0,
  }: {
    label: string;
    value: number;
    prevValue?: number;
    hint?: string;
    decimals?: number;
  }) => {
    const isZero = !value || Math.abs(value) < 0.0001;
    const showPrev = prevValue !== undefined && Number.isFinite(prevValue);
    const delta = showPrev ? value - (prevValue as number) : null;
    const showDelta = delta !== null && Math.abs(delta) >= 0.5;
    // Kosten: weniger = positiv (grün), mehr = negativ (rot)
    const deltaPositive = showDelta && (delta as number) < 0;
    const fmtAmt = (v: number) => (decimals > 0 ? fmt2(v) : fmt(v));
    return (
      <div
        className={`flex justify-between items-center py-1.5 px-3 bg-pe-tile-header dark:bg-slate-800 rounded mb-1 min-h-[40px] border border-pe-border dark:border-slate-700 ${
          isZero && !showPrev ? 'opacity-60' : ''
        }`}
      >
        <div className="flex flex-col pr-2 min-w-0">
          <span className="text-sm text-pe-muted dark:text-gray-300">{label}</span>
          {hint ? (
            <span className="text-[10px] text-pe-muted dark:text-slate-500 leading-tight mt-0.5">
              {hint}
            </span>
          ) : null}
          {showPrev ? (
            <span className="text-[10px] text-stone-500 dark:text-slate-500 mt-0.5 font-mono">
              Vorher {fmtAmt(prevValue as number)}
            </span>
          ) : null}
        </div>
        <div className="flex flex-col items-end shrink-0">
          <span className="text-[9px] uppercase tracking-wider text-stone-500 dark:text-slate-500 font-bold">
            Jetzt
          </span>
          <div className="flex items-baseline gap-2 justify-end">
            <span className="px-2 py-0.5 rounded text-center min-w-[96px] font-mono font-semibold whitespace-nowrap text-pe-accent-text dark:text-[#E6C299]">
              {fmtAmt(value)}
            </span>
            {showDelta ? (
              <span
                className={`font-mono font-bold text-sm whitespace-nowrap ${
                  deltaPositive
                    ? 'text-emerald-600 dark:text-emerald-400'
                    : 'text-rose-600 dark:text-rose-400'
                }`}
                title={deltaPositive ? 'Einsparung ggü. Vorher' : 'Mehrkosten ggü. Vorher'}
              >
                {(delta as number) > 0 ? '+' : ''}
                {fmtAmt(delta as number)}
              </span>
            ) : null}
          </div>
        </div>
      </div>
    );
  };

  const umsatzScale =
    snapshot.historischerMonatsUmsatzNetto > 0
      ? derived.monatsUmsatzNetto / snapshot.historischerMonatsUmsatzNetto
      : 1;

  const variableAnteil =
    derived.monatsUmsatzNetto * (snapshot.baseVariableKostenQuotePercent / 100);

  const histVarMonat =
    snapshot.historischerMonatsUmsatzNetto *
    (snapshot.baseVariableKostenQuotePercent / 100);

  const geraeteVorherEuro = Math.max(0, snapshot.baseGeraetekostenMonat || 0);
  const geraeteJetztEuro = knobs.geraeteAktiv ? geraeteVorherEuro : 0;

  /** Live-Näherung Gesamtkosten (ohne Solo-Anpassung): Fix + Var-Ist + Personal-Ist + Geräte-Ist */
  const gesamtkostenVorherNaeherung =
    snapshot.baseFixkostenNetto +
    histVarMonat +
    snapshot.basePersonalkostenIst +
    geraeteVorherEuro;

  return (
    <div className="bg-pe-elevated dark:bg-[#0f1015] rounded-xl border border-pe-border dark:border-slate-800 shadow-xl p-5 md:p-6 text-left">
      <p className="text-xs text-pe-muted dark:text-slate-400 mb-2 leading-relaxed">
        Transparenz Solo-Simulation: <strong>Vorher</strong> = Live-Akte, <strong>Jetzt</strong> =
        Solo. Versicherungen/Beiträge &amp; Orange-Kosten skalieren mit dem Solo-Umsatz. Alle Beträge
        = Ø Monat netto ({currencyCode}).
      </p>

      <SectionHeader title="Personal (Live-Akte → Solo = 0)" />
      <div className="bg-pe-tile-header dark:bg-slate-800 border border-pe-border dark:border-slate-700 rounded-lg py-2 px-1">
        <CostRow
          label="Ist-Personalkosten MA"
          prevValue={snapshot.basePersonalkostenIst}
          value={0}
          hint="In der Solo-Simulation komplett auf 0 € gesetzt (kein Team)."
        />
      </div>

      <SectionHeader title="Fixkosten-Whitelist (Sachkosten, ohne Personal)" />
      <div className="bg-pe-tile-header dark:bg-slate-800 border border-pe-border dark:border-slate-700 rounded-lg py-2 px-1">
        {(snapshot.fixCostLines || []).map((line) => (
          <CostRow
            key={line.key}
            label={line.label}
            prevValue={line.monatNetto}
            value={line.monatNetto}
            hint="Unverändert (Fix)"
          />
        ))}
        <div className="flex justify-between items-center py-2 px-3 mt-1 border-t border-pe-border-strong dark:border-slate-600 font-bold text-pe-text text-sm">
          <span>Summe Fixkosten</span>
          <span className="font-mono text-pe-accent-text dark:text-[#E6C299]">
            {fmt(snapshot.baseFixkostenNetto)}
          </span>
        </div>
      </div>

      <SectionHeader title="Umsatzgekoppelte Kosten (Versicherungen + Orange)" />
      <p className="text-[10px] text-pe-muted dark:text-slate-500 mb-1 px-1">
        Skalierung Ist→Solo: ×{umsatzScale.toLocaleString('de-DE', { maximumFractionDigits: 2 })}{' '}
        (Solo-Umsatz ÷ historischer Monatsumsatz)
      </p>
      <div className="bg-pe-tile-header dark:bg-slate-800 border border-pe-border dark:border-slate-700 rounded-lg py-2 px-1">
        {(snapshot.variableCostLines || []).map((line) => (
          <CostRow
            key={line.key}
            label={line.label}
            prevValue={line.monatNetto}
            value={line.monatNetto * umsatzScale}
            hint="BWA-Basis × Umsatzfaktor"
          />
        ))}
        <CostRow
          label={`Variable Quote gesamt ${snapshot.baseVariableKostenQuotePercent.toLocaleString(
            'de-DE',
            { maximumFractionDigits: 1, minimumFractionDigits: 1 }
          )} % × Solo-Umsatz`}
          prevValue={histVarMonat}
          value={variableAnteil}
          hint="Inkl. Versicherungen/Beiträge umsatzgekoppelt"
        />
      </div>

      <SectionHeader title="Stellschrauben-Effekte" />
      <div className="bg-pe-tile-header dark:bg-slate-800 border border-pe-border dark:border-slate-700 rounded-lg py-2 px-1">
        <CostRow
          label="Gemeinkosten-Anpassung"
          prevValue={0}
          value={knobs.gemeinkostenAnpassungNetto}
          hint={
            knobs.gemeinkostenAnpassungNetto < 0
              ? 'Negativ = Einsparung (von Fixkosten abgezogen)'
              : knobs.gemeinkostenAnpassungNetto > 0
                ? 'Positiv = Zusatzkosten'
                : 'Keine Anpassung'
          }
        />
        <CostRow
          label="Azubi (nur wenn aktiv)"
          prevValue={0}
          value={derived.azubiKostenMonat}
          hint={
            knobs.azubiAktiv
              ? `Vergütung ${fmt(knobs.azubiVerguetungBrutto)} · ${knobs.azubiWochenstunden} Std./Wo.`
              : 'Azubi nicht aktiviert'
          }
        />
        <CostRow
          label="Materialquote (Info, nicht in Gesamtkosten)"
          prevValue={
            snapshot.historischerMonatsUmsatzNetto *
            (Math.max(0, snapshot.baseMaterialQuotePercent) / 100)
          }
          value={derived.virtuellerMaterialaufwand}
          hint={`${snapshot.baseMaterialQuotePercent.toLocaleString('de-DE', {
            maximumFractionDigits: 1,
          })} % vom Solo-Umsatz — nur Anzeige / Kalkulator`}
        />
        <CostRow
          label="Geräte in Solo-Kalkulation"
          prevValue={geraeteVorherEuro}
          value={geraeteJetztEuro}
          hint={
            knobs.geraeteAktiv
              ? 'Fixbetrag Live Ø-Monat — komplett eingerechnet (nicht umsatzgekoppelt)'
              : 'Geräte komplett aus der Solo-Kalkulation genommen'
          }
        />
      </div>

      <SectionHeader title="Kalk. Unternehmerlohn (Solo-Chef)" />
      <div className="bg-pe-tile-header dark:bg-slate-800 border border-pe-border dark:border-slate-700 rounded-lg py-2 px-1">
        <CostRow
          label="Geplanter Unternehmerlohn (Ziel)"
          value={knobs.unternehmerLohnZiel}
          hint="Entnahme / Wunschgehalt aus Unternehmerwerte"
        />
        <CostRow
          label={`EK-Steueraufschlag ${knobs.ekSteuersatzPercent.toLocaleString('de-DE', {
            maximumFractionDigits: 0,
          })} %`}
          value={derived.unternehmerAufwand - knobs.unternehmerLohnZiel}
          hint="Aufwand = Ziel × (1 + EK%)"
        />
        <CostRow label="Unternehmeraufwand gesamt" value={derived.unternehmerAufwand} />
        <CostRow
          label="Prod. Stundenlohn Chef"
          value={derived.prodStundenlohnChef}
          decimals={2}
          hint={`${knobs.wochenstunden} Std./Wo. · für Kalkulator-Minutensatz`}
        />
      </div>

      <SectionHeader title="Solo-Kostenaufbau (Ergebnis)" />
      <div className="bg-pe-tile-header dark:bg-slate-800 border border-pe-border dark:border-slate-700 rounded-lg py-2 px-1">
        <CostRow
          label="Virtuelle Gemeinkosten"
          prevValue={
            snapshot.baseFixkostenNetto + histVarMonat + snapshot.basePersonalkostenIst
          }
          value={derived.virtuelleGemeinkosten}
          hint="Fix + Anpassung + variabel + Azubi (ohne UL, ohne Material)"
        />
        <CostRow label="+ Unternehmeraufwand" value={derived.unternehmerAufwand} />
        <CostRow
          label="+ Geräte"
          prevValue={geraeteVorherEuro}
          value={derived.virtuellerGeraeteaufwand}
        />
        <CostRow
          label="Gesamtkosten Solo (Sollumsatz netto)"
          prevValue={gesamtkostenVorherNaeherung}
          value={derived.gesamtKostenSolo}
          hint="Vorher ≈ Fix + Var + Personal-Ist + Geräte (Live); Jetzt = Solo"
        />
        <div className="flex justify-between items-center py-1.5 px-3 text-sm text-pe-muted dark:text-slate-400">
          <span>Ø realistisch (10 Mon.) netto</span>
          <span className="font-mono">{fmt(derived.kostendeckungInklUrlaubNetto)}</span>
        </div>
        <div className="flex justify-between items-center py-1.5 px-3 text-sm text-pe-muted dark:text-slate-400">
          <span>Gemeinkostenfaktor</span>
          <span className="font-mono">
            {(derived.virtuellerGemeinkostenfaktor * 100).toLocaleString('de-DE', {
              maximumFractionDigits: 1,
              minimumFractionDigits: 1,
            })}{' '}
            %
          </span>
        </div>
        <div className="flex justify-between items-center py-1.5 px-3 text-sm text-pe-muted dark:text-slate-400">
          <span>Prod. Stundenlohn Chef (Kontrolle)</span>
          <span className="font-mono">{fmt2(derived.prodStundenlohnChef)}</span>
        </div>
      </div>
    </div>
  );
};

const StellschraubeField: React.FC<{
  label: string;
  value: number;
  onChange: (v: number) => void;
  min?: number;
  max?: number;
  step?: number;
  suffix?: string;
}> = ({ label, value, onChange, min = 0, max = 100000, step = 1, suffix }) => {
  const [draft, setDraft] = useState(
    Number.isFinite(value) ? String(value).replace('.', ',') : '0'
  );
  const [focused, setFocused] = useState(false);

  useEffect(() => {
    if (!focused) {
      setDraft(Number.isFinite(value) ? String(value).replace('.', ',') : '0');
    }
  }, [value, focused]);

  const clamp = (v: number) => Math.min(max, Math.max(min, v));
  const nudge = (dir: -1 | 1) => {
    const next = clamp((Number.isFinite(value) ? value : 0) + dir * step);
    const rounded =
      step >= 1 ? Math.round(next / step) * step : Math.round(next * 1000) / 1000;
    onChange(clamp(rounded));
  };

  /** Einfachklick/Fokus: alles markieren. Doppelklick: Cursor für Feinjustierung. */
  const selectAllOnSingleActivate = (
    e: React.FocusEvent<HTMLInputElement> | React.MouseEvent<HTMLInputElement>
  ) => {
    const el = e.currentTarget;
    const detail = 'detail' in e ? e.detail : 1;
    if (detail > 1) return;
    requestAnimationFrame(() => {
      if (document.activeElement === el) el.select();
    });
  };

  const commit = (raw: string) => {
    const cleaned = String(raw ?? '')
      .replace(/\./g, '')
      .replace(',', '.')
      .replace(/[^0-9.-]/g, '');
    const n = parseFloat(cleaned);
    const next = clamp(Number.isFinite(n) ? n : 0);
    onChange(next);
    setDraft(String(next).replace('.', ','));
  };

  const btnClass =
    'h-8 w-8 shrink-0 rounded-lg border border-pe-border-strong dark:border-slate-700 bg-pe-card dark:bg-[#15171e] text-pe-accent-text dark:text-[#E6C299] font-bold text-base leading-none flex items-center justify-center hover:border-[#E6C299]/60 active:scale-95 transition-all select-none';

  const inputClass =
    'w-28 h-10 rounded-lg border border-pe-border-strong dark:border-slate-700 bg-pe-input dark:bg-[#08090b] px-2.5 text-right font-mono text-sm text-pe-text dark:text-white outline-none focus:border-pe-accent-text dark:focus:border-[#E6C299]/70 appearance-none [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none [-moz-appearance:textfield]';

  return (
    <div className="min-w-0 space-y-1.5">
      <div className="flex items-center justify-between gap-2">
        <label className="text-xs font-bold uppercase tracking-wider text-pe-muted dark:text-slate-400 leading-snug">
          {label}
        </label>
        <div className="flex items-center gap-1.5 shrink-0">
          <input
            type="text"
            inputMode="decimal"
            enterKeyHint="done"
            autoComplete="off"
            spellCheck={false}
            value={focused ? draft : Number.isFinite(value) ? String(value).replace('.', ',') : '0'}
            onFocus={(e) => {
              setFocused(true);
              setDraft(Number.isFinite(value) ? String(value).replace('.', ',') : '0');
              selectAllOnSingleActivate(e);
            }}
            onClick={selectAllOnSingleActivate}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={() => {
              setFocused(false);
              commit(draft);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') e.currentTarget.blur();
            }}
            className={inputClass}
          />
          {suffix ? (
            <span className="text-xs font-bold text-pe-muted dark:text-slate-500 w-9">{suffix}</span>
          ) : null}
        </div>
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          aria-label="Verringern"
          onClick={() => nudge(-1)}
          className={btnClass}
        >
          −
        </button>
        <input
          type="range"
          min={min}
          max={max}
          step={step}
          value={clamp(Number.isFinite(value) ? value : 0)}
          onChange={(e) => onChange(parseFloat(e.target.value) || 0)}
          className="w-full h-1.5 accent-[#E6C299] cursor-pointer min-w-0"
        />
        <button
          type="button"
          aria-label="Erhöhen"
          onClick={() => nudge(1)}
          className={btnClass}
        >
          +
        </button>
      </div>
    </div>
  );
};

/* ============================================================================
 * PAGE ROOT
 * ========================================================================== */
const SoloSimulatorPage: React.FC = () => {
  const clientData = useAppStore((s) => s.clientData);
  const settings = useAppStore((s) => s.settings);
  const liveCalc = useCalculationsSelector();
  const impersonatedClientId = useAdminStore((s) => s.impersonatedClientId);

  const [showKostenDetails, setShowKostenDetails] = useState(false);

  const snapshot = useSoloSimulationStore((s) => s.snapshot);
  const isHydrated = useSoloSimulationStore((s) => s.isHydrated);
  const hydrateFromLiveAkte = useSoloSimulationStore((s) => s.hydrateFromLiveAkte);
  const resetKnobs = useSoloSimulationStore((s) => s.resetKnobs);

  const monatsUmsatzBrutto = useSoloSimulationStore((s) => s.monatsUmsatzBrutto);
  const unternehmerLohnZiel = useSoloSimulationStore((s) => s.unternehmerLohnZiel);
  const wochenstunden = useSoloSimulationStore((s) => s.wochenstunden);
  const gemeinkostenAnpassungNetto = useSoloSimulationStore(
    (s) => s.gemeinkostenAnpassungNetto
  );
  const ekSteuersatzPercent = useSoloSimulationStore((s) => s.ekSteuersatzPercent);
  const azubiAktiv = useSoloSimulationStore((s) => s.azubiAktiv);
  const azubiVerguetungBrutto = useSoloSimulationStore((s) => s.azubiVerguetungBrutto);
  const azubiWochenstunden = useSoloSimulationStore((s) => s.azubiWochenstunden);
  const geraeteAktiv = useSoloSimulationStore((s) => s.geraeteAktiv !== false);

  const setMonatsUmsatzBrutto = useSoloSimulationStore((s) => s.setMonatsUmsatzBrutto);
  const setUnternehmerLohnZiel = useSoloSimulationStore((s) => s.setUnternehmerLohnZiel);
  const setWochenstunden = useSoloSimulationStore((s) => s.setWochenstunden);
  const setGemeinkostenAnpassungNetto = useSoloSimulationStore(
    (s) => s.setGemeinkostenAnpassungNetto
  );
  const setEkSteuersatzPercent = useSoloSimulationStore((s) => s.setEkSteuersatzPercent);
  const setAzubiAktiv = useSoloSimulationStore((s) => s.setAzubiAktiv);
  const setAzubiVerguetungBrutto = useSoloSimulationStore((s) => s.setAzubiVerguetungBrutto);
  const setAzubiWochenstunden = useSoloSimulationStore((s) => s.setAzubiWochenstunden);
  const setGeraeteAktiv = useSoloSimulationStore((s) => s.setGeraeteAktiv);

  useEffect(() => {
    const mandantUid =
      impersonatedClientId ||
      (() => {
        try {
          return sessionStorage.getItem('GOD_MODE_ID');
        } catch {
          return null;
        }
      })() ||
      auth.currentUser?.uid ||
      'unknown';
    const year =
      (typeof localStorage !== 'undefined' && localStorage.getItem('CURRENT_YEAR')) ||
      String(new Date().getFullYear());

    const built = buildSoloSnapshotFromLive({
      clientData,
      calculations: liveCalc,
      settings,
    });
    hydrateFromLiveAkte({ mandantUid, year, snapshot: built });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    impersonatedClientId,
    clientData?.bwaData,
    clientData?.unternehmerData?.einkommenssteuer,
    clientData?.unternehmerData?.entnahme,
    clientData?.unternehmerData?.entnahmeNetto,
    clientData?.unternehmerData?.unternehmerlohn,
    clientData?.unternehmerData?.sozialkosten,
    clientData?.unternehmerData?.prodWochenstunden,
    clientData?.isVatExempt,
    settings?.country?.MwSt_Satz,
    liveCalc?.geraetekostenfaktor,
    liveCalc?.geraetekostenMonat,
  ]);

  const knobs: SoloSimulationKnobs = useMemo(
    () => ({
      monatsUmsatzBrutto,
      unternehmerLohnZiel,
      wochenstunden,
      gemeinkostenAnpassungNetto,
      ekSteuersatzPercent,
      azubiAktiv,
      azubiVerguetungBrutto,
      azubiWochenstunden,
      geraeteAktiv,
    }),
    [
      monatsUmsatzBrutto,
      unternehmerLohnZiel,
      wochenstunden,
      gemeinkostenAnpassungNetto,
      ekSteuersatzPercent,
      azubiAktiv,
      azubiVerguetungBrutto,
      azubiWochenstunden,
      geraeteAktiv,
    ]
  );

  const derived: SoloSimulationDerived = useMemo(
    () => computeSoloDerived(snapshot, knobs),
    [snapshot, knobs]
  );

  const materialListe: MatItem[] = useMemo(() => {
    const list =
      (clientData as any)?.kalkulator?.materialListe ||
      settings?.materialListe ||
      [];
    return Array.isArray(list)
      ? list.map((m: any) => ({
          id: String(m.id ?? m.name ?? Math.random()),
          name: String(m.name || 'Material'),
          gebinde: Number(m.gebinde) || 0,
          ekPreis: Number(m.ekPreis) || 0,
        }))
      : [];
  }, [clientData, settings?.materialListe]);

  const geraeteListe: DevItem[] = useMemo(() => {
    if (!geraeteAktiv) return [];
    const list = liveCalc?.geraeteListe || [];
    return (Array.isArray(list) ? list : [])
      .filter((g: any) => g && g.id)
      .map((g: any) => ({
        id: String(g.id),
        name: String(g.name || 'Gerät'),
        kostenProzent: Number(g.kostenProzent) || 0,
      }));
  }, [liveCalc?.geraeteListe, geraeteAktiv]);

  const barometerStundenfaktor = Number(
    (clientData as any)?.kalkulator?.barometerStundenfaktor ?? 0
  );

  const currencyCode = currencyLabel(
    liveCalc?.currencyCode || settings?.country?.currency,
    clientData?.Land || clientData?.land || settings?.selectedCountryId
  );
  const formatCurrency = useMemo(
    () => makeFormatCurrency(currencyCode),
    [currencyCode]
  );

  const kurzCalc: SoloKurzCalc = useMemo(() => {
    const vat = snapshot?.mwstFaktor && snapshot.mwstFaktor > 0 ? snapshot.mwstFaktor : 1.19;
    // Realistischer Ø-Monatsbedarf: Kosten decken bei nur 10 produktiven Monaten (×1,2)
    const realistischNetto = derived.kostendeckungInklUrlaubNetto;
    const liveGeraete = Number(liveCalc?.geraetekostenfaktor) || 0;
    return {
      gemeinkostenfaktor: derived.virtuellerGemeinkostenfaktor,
      materialkostenfaktor: derived.materialkostenfaktor,
      geraetekostenfaktor: derived.geraetekostenfaktor,
      prodLohnkostenfaktor: derived.prodLohnkostenfaktor,
      prevGemeinkostenfaktor: Number(liveCalc?.gemeinkostenfaktor) || 0,
      prevMaterialkostenfaktor: Number(liveCalc?.materialkostenfaktor) || 0,
      prevGeraetekostenfaktor: liveGeraete,
      prevProdLohnkostenfaktor: Number(liveCalc?.prodLohnkostenfaktor) || 0,
      monatsumsatzNetto: derived.monatsUmsatzNetto,
      monatsumsatzBrutto: monatsUmsatzBrutto,
      sollumsatzNetto: derived.gesamtKostenSolo,
      sollumsatzBrutto: derived.gesamtKostenSolo * vat,
      realistischNetto,
      realistischBrutto: derived.kostendeckungInklUrlaubBrutto,
      ueberschuss: derived.ueberschussNetto,
      ueberschussProzent: derived.ueberschussProzent,
      gesamtlohn: derived.unternehmerAufwand,
      rentabilitaetSoll: derived.rentabilitaetSoll,
      inhaberErtragSoll: derived.inhaberErtragSoll,
      currencyCode,
    };
  }, [
    derived,
    snapshot,
    monatsUmsatzBrutto,
    currencyCode,
    liveCalc?.gemeinkostenfaktor,
    liveCalc?.materialkostenfaktor,
    liveCalc?.geraetekostenfaktor,
    liveCalc?.prodLohnkostenfaktor,
  ]);

  const mwstSatz =
    snapshot && snapshot.mwstFaktor >= 1
      ? Number(((snapshot.mwstFaktor - 1) * 100).toFixed(2))
      : 19;

  return (
    <div className="min-h-screen bg-pe-bg dark:bg-[#030303] text-pe-text p-4 pb-32 relative">
      <SoloSystemStyles />

      <div className="mx-auto max-w-screen-2xl space-y-6">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 border-b border-pe-border dark:border-slate-800 pb-4">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] mb-1.5 text-pe-accent-text dark:text-[#E6C299]">
              PreisExpert · Solo-Simulator
            </p>
            <h1 className="text-2xl md:text-3xl font-black uppercase tracking-widest text-pe-text dark:text-white">
              Solo-Simulator
            </h1>
          </div>
          <button
            type="button"
            onClick={resetKnobs}
            className="self-start md:self-auto px-4 py-2 rounded-lg border border-pe-border-strong dark:border-slate-700 bg-pe-card dark:bg-[#15171e] text-xs font-bold uppercase tracking-widest text-pe-muted dark:text-slate-300 hover:border-[#E6C299]/50 hover:text-[#E6C299] transition-colors"
          >
            Parameter zurücksetzen
          </button>
        </div>

        {!isHydrated || !snapshot ? (
          <div className="bg-pe-elevated dark:bg-[#0f1015] border border-pe-border dark:border-slate-800 rounded-xl p-8 text-sm text-pe-muted">
            Snapshot wird aus der geöffneten Mandantenakte geladen…
          </div>
        ) : (
          <>
            {/* ========== ZONE 1: STELLSCHRAUBEN ========== */}
            <section className="bg-pe-elevated dark:bg-[#0f1015] rounded-xl border border-pe-border dark:border-slate-800 shadow-xl overflow-hidden">
              <div className="bg-pe-card dark:bg-[#15171e] px-5 py-3 border-b border-pe-border dark:border-slate-800 flex flex-wrap items-center justify-between gap-3">
                <h2 className="text-sm font-bold uppercase tracking-wide text-pe-accent-text dark:text-[#E6C299]">
                  Stellschrauben
                </h2>
                <div className="flex flex-wrap items-center gap-4">
                  <label className="flex items-center gap-2 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={geraeteAktiv}
                      onChange={(e) => setGeraeteAktiv(e.target.checked)}
                      className="h-4 w-4 accent-[#E6C299]"
                    />
                    <span className="text-xs font-bold uppercase tracking-wider text-pe-muted dark:text-slate-400">
                      Geräte einkalkulieren
                    </span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer select-none">
                    <input
                      type="checkbox"
                      checked={azubiAktiv}
                      onChange={(e) => setAzubiAktiv(e.target.checked)}
                      className="h-4 w-4 accent-[#E6C299]"
                    />
                    <span className="text-xs font-bold uppercase tracking-wider text-pe-muted dark:text-slate-400">
                      Azubi aktivieren
                    </span>
                  </label>
                </div>
              </div>

              <div className="p-5 md:p-6 space-y-5">
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
                  <StellschraubeField
                    label="Geschätzter Monatsumsatz Solo brutto"
                    value={monatsUmsatzBrutto}
                    onChange={setMonatsUmsatzBrutto}
                    min={0}
                    max={80000}
                    step={100}
                    suffix={currencyCode}
                  />
                  <StellschraubeField
                    label="prod. Wochenstunden"
                    value={wochenstunden}
                    onChange={setWochenstunden}
                    min={1}
                    max={60}
                    step={1}
                    suffix="Std"
                  />
                  <StellschraubeField
                    label="Geplanter Unternehmerlohn"
                    value={unternehmerLohnZiel}
                    onChange={setUnternehmerLohnZiel}
                    min={0}
                    max={20000}
                    step={100}
                    suffix={currencyCode}
                  />
                  <StellschraubeField
                    label="EK Steuersatz"
                    value={ekSteuersatzPercent}
                    onChange={setEkSteuersatzPercent}
                    min={0}
                    max={55}
                    step={1}
                    suffix="%"
                  />
                  <StellschraubeField
                    label="Gemeinkosten-Anpassung (netto)"
                    value={gemeinkostenAnpassungNetto}
                    onChange={setGemeinkostenAnpassungNetto}
                    min={-10000}
                    max={10000}
                    step={100}
                    suffix={currencyCode}
                  />
                </div>

                {azubiAktiv && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 pt-4 border-t border-pe-border dark:border-slate-800">
                    <StellschraubeField
                      label="Azubi Vergütung (brutto)"
                      value={azubiVerguetungBrutto}
                      onChange={setAzubiVerguetungBrutto}
                      min={0}
                      max={3000}
                      step={100}
                      suffix={currencyCode}
                    />
                    <StellschraubeField
                      label="Wochenstunden abzügl. Schulzeit"
                      value={azubiWochenstunden}
                      onChange={setAzubiWochenstunden}
                      min={0}
                      max={40}
                      step={1}
                      suffix="Std"
                    />
                  </div>
                )}

                <p className="text-xs text-pe-muted dark:text-slate-500 leading-snug">
                  Gemeinkosten-Anpassung: negativ = Einsparung, positiv = Zusatzkosten. EK-Steuersatz
                  bleibt editierbar, wenn der Unternehmerlohn angepasst wird.
                </p>
              </div>
            </section>

            {/* Details-Haken (wie Lohnfaktor, ohne Passwort) */}
            <div className="flex justify-end">
              <div className="bg-pe-tile-header dark:bg-slate-700 p-2 rounded-lg shadow-lg border border-pe-border dark:border-slate-500">
                <label className="flex items-center space-x-2 text-pe-text cursor-pointer">
                  <input
                    type="checkbox"
                    checked={showKostenDetails}
                    onChange={(e) => setShowKostenDetails(e.target.checked)}
                    className="form-checkbox h-5 w-5 text-blue-600 bg-gray-800 border-gray-600 rounded focus:ring-blue-500"
                  />
                  <span className="font-semibold text-sm">Details anzeigen</span>
                </label>
              </div>
            </div>

            {showKostenDetails && snapshot ? (
              <SoloKostenDetails
                snapshot={snapshot}
                derived={derived}
                knobs={knobs}
                currencyCode={currencyCode}
                formatCurrency={formatCurrency}
              />
            ) : null}

            {/* ========== ZONE 2: KURZAUSWERTUNG ========== */}
            <SoloKurzauswertung calc={kurzCalc} />

            {/* ========== ZONE 3: KALKULATOR ========== */}
            <SoloHauptKalkulator
              materialData={materialListe}
              geraeteListe={geraeteListe}
              gemeinkostenfaktor={derived.virtuellerGemeinkostenfaktor}
              prodStundenlohnChef={derived.prodStundenlohnChef}
              mwstSatz={mwstSatz}
              verlustPercent={snapshot.verlustPercent}
              lohnfortzahlungPercent={snapshot.lohnfortzahlungPercent}
              matFaktor={snapshot.warenwirtschaftAufschlagVk}
              liquiditaetPercent={snapshot.liquiditaetPercent}
              formatCurrency={formatCurrency}
              currencyCode={currencyCode}
              barometerStundenfaktor={barometerStundenfaktor}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default SoloSimulatorPage;
